/* =======================================================================
   Knowledge Base — interactive walkthrough. Azure item 416, phase 3.

   "Take me there" in an article hands the reader to the real screen and
   talks them through it: the page dims, a rounded hole is left around the
   one control that matters, and a tooltip points at it. When the reader
   does the thing, it moves on by itself. At the end the contrast comes back
   and a small badge stays in the header as the way back in.

   Ported from the mockup on 10 Sep 2026. The engine is deliberately kept
   imperative and DOM-driven (refs, not React state, for the overlay
   geometry): it is the tested code, and geometry that re-renders through
   React on every scroll would be both slower and harder to reason about.

   Two decisions worth keeping if this grows:

   * Steps advance off STATE, not off clicks. Each step owns a done()
     predicate read every 100ms, so the tour cannot fall out of step with
     the app and needs no hooks inside the app's click handlers. It also
     makes Resume land on the first thing still to do.

   * Whenever a walkthrough brings the reader to a new screen, the next step
     is a view step ({ kind:'info', view:true }, no target): one undimmed tile
     saying what the screen shows, moved on only by Next. Readers who click
     straight through otherwise never learn where they have landed.

   * Targets are data-tour="…" attributes on the real Work Orders screen
     (BranchBar, CreateButton, WO.js, createwomodal.js, WoTable). Restyle
     freely; the tour keeps working. The app exposes two things the tour
     cannot read from the URL or DOM through kbSession.js: how many work
     orders have been created this session, and whether a tour is running.
   ======================================================================= */
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { KB_BASE, KB_WALK_START, SESSION, announce, woCreatedCount, woLastCreatedId, techsSavedCount, woEditedCount, filesUploadedCount, poCreatedCount, navPick, clearNavPick, setWalkthroughRunning, setLastArticle, readJson, writeJson } from './kbSession';
import './kb.css';

/* ---------------- what the real app tells us ---------------- */
const qs = () => new URLSearchParams(window.location.search);
const onWorkList = () => window.location.pathname === '/work';
const branchPicked = () => onWorkList() && Number(qs().get('branch') || 0) > 0;
const dialogOpen = () => onWorkList() && qs().get('create') === '1';
const searching = () => onWorkList() && !!(qs().get('keyword') || '').trim();
const filtered = () => onWorkList() && !!qs().get('criteria');
const filterOpen = () => onWorkList() && !!document.querySelector('[data-tour="wo-filter-panel"]');
const filled = key => !!document.querySelector('[data-tour="' + key + '"][data-tour-filled]');
/* a step's target is a selector, or a function returning one — the finish step
   points at the row that was just created, whose id is not known in advance */
const sel = s => (typeof s.target === 'function' ? s.target() : s.target);
/* ---- a step that plays itself in ----
   One clock: how long the reader has been on the step they are on, for a step
   that brings itself in a piece at a time rather than arriving whole.
   animateFor: <ms> tells the engine to keep repainting while that happens. */
let stepSince = 0;
const since = () => Date.now() - stepSince;
/* ---- hover to discover ----
   Where the pointer is, kept by the engine while a walkthrough runs, so a
   step can say "this card belongs to whatever the reader is pointing at".
   The ring's own padding counts as part of the control: the card should not
   flicker off when the pointer strays onto the highlight around it. */
let mousePos = null;
const hovering = q => {
  const el = mousePos && document.querySelector(q);
  if (!el) return false;
  const b = el.getBoundingClientRect(), pad = 8;
  return b.width > 0 && b.height > 0 &&
    mousePos.x >= b.left - pad && mousePos.x <= b.right + pad &&
    mousePos.y >= b.top - pad && mousePos.y <= b.bottom + pad;
};

/* ---- a hover card the reader can reach ----
   hovering() on its own suits a card that is only ever read: up while the
   pointer is on the control, gone the moment it leaves. A card with something
   in it to reach — a link — needs three things more. It has to survive the
   trip from the control across the gap to the card; it has to stay up while
   the pointer is on the card itself; and it must not appear merely because
   the pointer crossed the empty space where it would have been, or the page
   grows tooltips the reader never asked for.

   So: only the control can raise it, the card can only keep it up, and
   leaving both starts a short grace period instead of hiding at once. The
   watcher's own tick is what expires the grace — the pointer may well have
   stopped moving, and a card must not hang there because no mousemove came. */
const latches = {};
const LATCH_GRACE = 420;      /* ms to cross the gap — generous, it is a small target */
const hoveringCard = key => {
  const c = document.querySelector('.wt-callout[data-wt-card="' + key + '"]');
  /* already fading out, or never shown: pointing at it does not bring it back */
  if (!c || c.classList.contains('hide') || c.classList.contains('duck')) return false;
  const b = c.getBoundingClientRect(), pad = 8;
  return !!mousePos && b.width > 0 &&
    mousePos.x >= b.left - pad && mousePos.x <= b.right + pad &&
    mousePos.y >= b.top - pad && mousePos.y <= b.bottom + pad;
};
const hoverLatch = (key, q) => {
  const L = latches[key] || (latches[key] = { up: false, left: 0 });
  if (hovering(q) || (L.up && hoveringCard(key))){ L.up = true; L.left = 0; return true; }
  if (!L.up) return false;
  if (!L.left) L.left = Date.now();
  if (Date.now() - L.left < LATCH_GRACE) return true;
  L.up = false; L.left = 0;
  return false;
};
const resetLatches = () => { Object.keys(latches).forEach(k => delete latches[k]); };

/* ---- the gate ----
   One step in a walkthrough may guard a control that does something real —
   SUBMIT on the create form makes a work order the whole company will see.
   That step puts a card beside the control and leaves the dim over it until
   the reader answers; their answer is what the step branches on. */
let askAnswer = null;
const resetAsk = () => { askAnswer = null; };
const askedYes = () => askAnswer === 'yes';
const askedNo = () => askAnswer === 'no';

/* a step field that may be a plain value or a function of the moment */
const val = v => (typeof v === 'function' ? v() : v);

/* ---- the four ways of narrowing the work order list ----
   One step each: the tile stands beside the control, the control is lit, and
   Next moves on to the next one. Kept together so the wording and the order
   read as the one explanation they are. */
const WO_WAYS = [
  { target: '[data-tour="wo-search"]', place: 'bottom', title: 'Search',
    /* Once there is a search in place the results are lit and clickable too:
       a reader who only wanted this one job can take it and go, without
       being walked through the other three ways of finding it. */
    extra: () => (searching() ? '[data-tour="wo-table"]' : null),
    also: '[data-tour="wo-search"] ul', placeOnMain: true,
    body: () => '<p>Anything about the job goes here — its number, the customer, or a word from the description.</p>' +
      (searching() ? '<p><b>There it is?</b> Click it in the list and we are done.</p>' : '') },
  { target: '[data-tour="wo-branches"]', place: 'bottom', title: 'Branches',
    body: '<p>Pick a branch to cut the list down to it. <span class="k">Filter</span> only turns up once you have.</p>',
    /* Picking one here is exactly what the filter steps are waiting for, so
       take the reader straight there rather than through the two sorting
       tiles and the hand-over. Back brings them back to this tile. */
    prep(){ markBranch(); },
    skipTo: () => (branchSince() ? 'filter-here' : null) },
  { target: '[data-tour="wo-sort-number"]', place: 'bottom', title: 'WO#',
    body: '<p>Click the heading to sort by number, and again to turn the order round.</p>' },
  { target: '[data-tour="wo-sort-date"]', place: 'bottom', title: 'Date Started',
    body: '<p>The only other heading that sorts — newest first, or oldest first.</p>',
    /* it sits off the end of a table that scrolls sideways */
    prep(){ woScroll('[data-tour="wo-sort-date"]'); } }
];
/* Which branch was showing when the tile that asks went up, so that "they
   have picked a branch" means a fresh choice and not the one they arrived on.
   Two steps ask (the Branches tile and the hand-over): whichever is up owns it. */
let branchAt = null;
const markBranch = () => { branchAt = qs().get('branch') || '0'; };
const branchSince = () => branchAt !== null && (qs().get('branch') || '0') !== branchAt;

/* the work order table scrolled so a heading of it is in view (or back to the
   work order numbers, with no heading named) */
const woScroll = q => {
  const t = document.querySelector('[data-tour="wo-table"]');
  if (!t) return;
  const el = q && document.querySelector(q);
  const tb = t.getBoundingClientRect(), b = el && el.getBoundingClientRect();
  const to = !b ? 0
    : b.right > tb.right - 8 ? t.scrollLeft + (b.right - tb.right) + 16
    : b.left < tb.left + 8 ? t.scrollLeft + (b.left - tb.left) - 16 : t.scrollLeft;
  if (to === t.scrollLeft) return;
  if (t.scrollTo) t.scrollTo({ left: to, behavior: 'smooth' }); else t.scrollLeft = to;
};

/* ---------------- the walkthroughs ---------------- */
/* Has the reader created a work order since this walkthrough was last set
   up? Submitting clears the form, so the form cannot answer it; the session
   counter only ever goes up, so a mark taken at reset() survives everything
   except deliberately starting again. */
let wtMark = 0;
const wtMadeOne = () => woCreatedCount() > wtMark;

/* Marks for the find walkthrough, set by its own done() predicates: once the
   reader has searched, that stays true even after they clear the search —
   which is the next step. Cleared by its reset(). */
const seen = { search: false };

/* For the assign walkthrough. The work order page and the Assign window hold
   their state in React, so the tour reads the DOM: data-tour anchors in
   ServiceCall.jsx and the AssignTech components, with each row carrying its
   chosen user and role as data-tour-user / data-tour-role (-1 = none). */
const onDetail = () => /^\/work\/\d+\/?$/.test(window.location.pathname);
/* The other way readers find a job (Gavin, 22 Sep): the Customers tab, then
   the customer, then one of its sites, whose page lists that site's work
   orders. Customers and locations are the same journey, so one test covers
   the whole branch. */
const onCustomerPath = () => /^\/(customer|location)(\/|$)/.test(window.location.pathname);
const onCustomerAccount = () => /^\/customer\/\d+/.test(window.location.pathname);
const onLocationPage = () => /^\/location\/\d+/.test(window.location.pathname);
/* The two halves of the find walkthrough, as step when() conditions. The fork
   is decided by which tab the reader clicked (kbSession's navPick), not by
   where they happen to be: the tour starts wherever they are, so "on the work
   order list" would pick a side before they had chosen one. Until they click,
   navPick is '' and neither branch is on. */
const onWorkBranch = () => navPick() === 'work';
const onCustomerBranch = () => navPick() === 'customers';
const assignOpen = () => !!document.querySelector('[data-tour="wo-assign-dialog"]');
/* A Requested work order cannot be assigned to anyone: the button for it is
   not rendered until the request is approved. The badge beside the service
   type is the only place the status is shown on this page. */
const jobRequested = () => {
  const b = document.querySelector('[data-testid="wo-status-badge"]');
  return !!b && /requested/i.test(b.textContent || '');
};
const assignRows = () => [...document.querySelectorAll('[data-tour="assign-row"]')]
  .map(r => ({ u: String(r.dataset.tourUser), r: String(r.dataset.tourRole) }));
let techMark = 0;                                   /* saves counted before this run */
const savedTechs = () => techsSavedCount() > techMark;
let teamAtOpen = null;                              /* users in the rows when Team was reached */
/* somebody new in a row since the window opened — "picked someone", not a
   name that was already on the job */
const teamPicked = () => !!teamAtOpen && assignRows().some(r => r.u !== '-1' && !teamAtOpen.includes(r.u));

/* For the edit walkthrough. The pencil opens the create dialog in edit mode
   (createwomodal.js with an order), on the work order page itself. */
const editOpen = () => onDetail() && !!document.querySelector('[data-tour="wo-dialog"]');
let editMark = 0;                                   /* edits saved before this run */
const savedEdit = () => woEditedCount() > editMark;

/* For the upload walkthrough. The four sections share one upload window
   (uploadModalUI's ModalShell), told apart by its title; which one the
   reader opened decides the wording later and where the finish points. */
const UPLOAD_KINDS = [
  ['photos', /photo/i, 'Photos/Videos'], ['docs', /document/i, 'Technical Documents'],
  ['proposals', /proposal/i, 'Proposals'], ['invoices', /invoice/i, 'Invoices']];
let uploadKind = null;
const uploadDialog = () => {
  if (!onDetail()) return null;
  const d = [...document.querySelectorAll('[data-tour="modal-shell"]')]
    .find(el => /^\s*upload/i.test((el.querySelector('h2') || {}).textContent || ''));
  if (d){ const t = d.querySelector('h2').textContent; const k = UPLOAD_KINDS.find(([, re]) => re.test(t)); if (k) uploadKind = k[0]; }
  return d || null;
};
const uploadOpen = () => !!uploadDialog();
const uploadStaged = () => uploadOpen() && !document.querySelector('[data-tour="upload-dropzone"]');
const uploadLabel = () => { const k = UPLOAD_KINDS.find(([key]) => key === uploadKind); return k ? k[2] : 'the section'; };
let uploadMark = 0;                                 /* uploads before this run */
const uploaded = () => filesUploadedCount() > uploadMark;

/* For the create-purchase-order walkthrough. Every PO in Makani hangs off a
   work order, so this tour starts the same way the other work-order ones do
   and the window it opens belongs to the job, not to the PO tab. */
const poOpen = () => !!document.querySelector('[data-tour="po-dialog"]');
/* which half of the window is showing. The tour forks on it, and the reader
   can change their mind at any point up to SUBMIT — the fork follows them. */
const poMode = () => { const m = document.querySelector('[data-tour="po-mode"]'); return m && m.getAttribute('data-tour-mode'); };
const poSimple = () => poMode() === 'simple';
const poDetailed = () => poMode() === 'detailed';
let poMark = 0;                                     /* POs raised before this run */
const poMade = () => poCreatedCount() > poMark;

/* For the timesheet walkthrough. The section is a WoSectionCard and it starts
   CLOSED: collapsed children stay in the DOM at zero height, so "is the table
   there?" answers yes while the reader can see nothing. The card reports its
   own state instead — data-tour-open — and every predicate here reads that. */
const tsOpen = () => onDetail() && !!document.querySelector('[data-tour="wo-timesheet-body"][data-tour-open="1"]');
const tsView = () => { const v = document.querySelector('[data-tour="ts-view"]'); return v && v.getAttribute('data-tour-view'); };
const tsDay = () => tsOpen() && tsView() === 'day';
/* editing a row: the three boxes report it, so this does not depend on which
   row the reader chose */
const tsEditing = () => !!document.querySelector('[data-tour="ts-fields"][data-tour-editing="1"]');

/* For the service ticket walkthrough. Before Completed the SERVICE TICKET
   button builds a view-only PDF preview (wo-st-preview → st-preview); from
   Completed on it is the blue button that opens the editing page instead
   (wo-st-open), which has its own walkthrough. */
const stPreviewOpen = () => onDetail() && !!document.querySelector('[data-tour="st-preview"]');
const completedJob = () => onDetail() && !!document.querySelector('[data-tour="wo-st-open"]');

/* Scroll a container to a new position so the reader sees where they are
   being taken: ease-in-out, duration by distance, with the overlay's rings
   and tiles hidden throughout (.wt.arriving) and popped in once the page has
   settled. Frames come from rAF where it ticks and a timer where it does not
   (see the watcher); a reader who scrolls mid-way takes over and the tiles
   appear at once. Reduced motion jumps, then pops in the same way. */
let arrivals = 0;     /* scrollers still moving — glideTo may run several at once */
const scrollWithArrival = (sc, delta) => {
  const wt = document.querySelector('.kb-wt .wt');
  if (!sc || Math.abs(delta) < 4){ return; }
  const from = sc.scrollTop, to = Math.max(0, Math.min(from + delta, sc.scrollHeight - sc.clientHeight));
  /* Counted, not a flag: the page and the list inside it are scrolled together
     and finish at different times, and the first to land must not pop the
     tiles back while the other is still moving. */
  const land = () => setTimeout(() => {
    if (--arrivals <= 0){ arrivals = 0; if (wt) wt.classList.remove('arriving'); }
  }, 180);                                                /* after .scrolling clears */
  if (wt){ arrivals++; wt.classList.add('arriving'); }
  const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduce){ sc.scrollTop = to; land(); return; }
  const dist = Math.abs(to - from), dur = Math.min(1100, Math.max(500, 380 + dist * 0.45));
  const ease = t => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);   /* easeInOutCubic */
  const t0 = performance.now();
  let expected = from;
  const tick = () => {
    if (Math.abs(sc.scrollTop - expected) > 3){ land(); return; }          /* the reader took over */
    const t = Math.min(1, (performance.now() - t0) / dur);
    expected = Math.round(from + (to - from) * ease(t));
    sc.scrollTop = expected;
    if (t < 1) frame(); else land();
  };
  const frame = () => {
    let fired = false;
    const once = () => { if (!fired){ fired = true; tick(); } };
    if (window.requestAnimationFrame) window.requestAnimationFrame(once);
    setTimeout(once, 20);
  };
  frame();
};

/* The arrival pop-in on its own, for a step that lights several controls
   without having to scroll to them first: hide the rings and tiles for a
   beat, then let them fade up in place (see scrollWithArrival). */
const popInTiles = () => {
  const wt = document.querySelector('.kb-wt .wt');
  if (!wt) return;
  wt.classList.add('arriving');
  setTimeout(() => wt.classList.remove('arriving'), 160);
};

/* ---- backing out ----
   A step that moved the reader — opened a window, closed one, went to another
   page — has to put that back when they press Back, or the step behind it
   ends up describing a screen they are not looking at. onBack does it; this
   is the usual body of one.  See WALKTHROUGH-ENGINE.md, "Back must undo". */
const closeWith = q => { const el = document.querySelector(q); if (el) el.click(); };

/* Bring a control into view, gently, for a step whose target may be anywhere
   down a long page depending on what the reader was reading a moment ago. The
   tiles and rings pop in when it lands.

   Every scroller between the control and the window is moved, not just the
   nearest one. A row inside a list inside a page needs both: scrolling only
   the list leaves the control neatly centred in a list that is itself off the
   screen. The deltas can all be worked out up front and run together, because
   each is measured between the control and one scroller — a difference an
   outer scroller moving does not change.

   align decides where a control that is TALLER than the space it has to fit in
   comes to rest. Centring something taller than the view puts both of its ends
   off the screen, which is the whole complaint. So the step says which end it
   cares about: 'top' for a section whose heading anchors the tile, 'bottom'
   for a list whose newest entry is at the end. Anything that fits is centred
   as before, and align is ignored. */
const glideTo = (q, opts) => {
  const el = document.querySelector(q);
  if (!el) return;
  const align = (opts && opts.align) || 'top', pad = 16;
  const b = el.getBoundingClientRect();
  const moves = [];
  let sc = el.parentElement;
  while (sc && sc !== document.body){
    const cs = getComputedStyle(sc);
    if (/auto|scroll/.test(cs.overflowY) && sc.scrollHeight > sc.clientHeight){
      const r = sc.getBoundingClientRect();
      const band = Math.min(r.height, sc.clientHeight);
      moves.push([sc, b.height > band - pad * 2
        ? (align === 'bottom' ? (b.bottom + pad) - (r.top + band) : (b.top - pad) - r.top)
        : (b.top + b.bottom) / 2 - (r.top + band / 2)]);
    }
    /* nothing above a fixed ancestor scrolls it */
    if (cs.position === 'fixed') break;
    sc = sc.parentElement;
  }
  moves.forEach(m => scrollWithArrival(m[0], m[1]));
};

/* Back out of a step that scrolled the page down: the same scroller, back to
   the top, so the tile behind it is describing the screen it was written for. */
const glideTop = q => {
  const el = document.querySelector(q);
  if (!el) return;
  let sc = el.parentElement;
  while (sc && sc !== document.body &&
    !(/auto|scroll/.test(getComputedStyle(sc).overflowY) && sc.scrollHeight > sc.clientHeight)) sc = sc.parentElement;
  if (!sc || sc === document.body) return;
  scrollWithArrival(sc, -sc.scrollTop);
};

/* Shared opening for any walkthrough that works on one work order: find it
   (an open step) and then say what the work order page is (a view step), so
   the reader never has to leave the tour to go looking for the job. */

/* An open step. Finding a job can take any route — search, a branch, the
   filter, or just spotting it — so nothing is dimmed but the app's side menu,
   every tool is outlined, and the one thing that moves the tour on is opening
   a work order. */
const findJobStep = () => ({
  dimOnly: '[data-tour="app-sidemenu"]', dock: true, noSkip: true,
  /* sorting left out on purpose: rarely how anyone finds a job (Gavin) */
  outline: ['[data-tour="wo-search"]', '[data-tour="wo-branches"]', '[data-tour="wo-filter"]'],
  also: '[data-tour="wo-search"] ul, [data-tour="wo-filter-panel"] ul',
  yieldTo: '[data-tour="date-picker"]',
  title: 'Find your work order',
  body: () => '<p>Use whichever is quickest — everything outlined works:</p>' +
        '<ul>' +
        '<li><b>Search</b> for the work order number, the customer, or a word from the description.</li>' +
        '<li><b>Pick its branch</b>' + (document.querySelector('[data-tour="wo-filter"]')
          ? ', then <span class="k">Filter</span> by status, customer, technician and more.'
          : ' — that also brings up <span class="k">Filter</span>.') + '</li>' +
        '<li>Or just <b>click it</b> in the list if you can already see it.</li>' +
        '</ul>',
  doIt: 'Click the work order when you find it',
  done: () => onDetail() });

/* New screen, so one tile says what is on it — for readers who click straight
   through. Also where a start from an open work order lands. `next` finishes
   "when you're ready to …"; `done` is the walkthrough's own "behind you". */
const viewJobStep = (next, done) => ({
  kind: 'info', view: true,
  /* Back from here is the step that found this job, which lives on the list —
     a different page, so going back has to take the reader there too. */
  onBack(navigate){ navigate('/work?branch=0'); },
  title: 'This is the work order',
  body: '<p>Everything about this one job lives on this page. From the top: the customer’s details, ' +
        'the service panel — type, status, NTE, description, and on the right who is <b>Assigned To</b> — ' +
        'then photos, proposals, notes, documents, invoices, purchase orders, the timesheet and its history.</p>' +
        '<p>Press <b>Next</b> when you’re ready to ' + next + '.</p>',
  done });

/* WO-06's Forms card: raised by the button, kept up by the card itself */
const formsHover = () => hoverLatch('forms', '[data-tour="wo-add-form"]');

/* Exported for walkthroughs.test.js, which checks every definition against
   the anchors that actually exist in the app. Nothing else imports it. */
export const WALKTHROUGHS = {
  'create-work-order': {
    label: 'Create a work order',
    /* The screen the article's first step assumes: the list, on All, nothing
       open. Work orders already created are left alone. */
    reset(navigate){
      wtMark = woCreatedCount();
      resetAsk();
      navigate('/work?branch=0', { replace: true });
    },
    /* Work in progress worth not throwing away: the dialog open, or a
       required field already chosen. A branch on its own does not count —
       reselecting one is the walkthrough's own first step — and neither does
       a finished job, or the demo could never be run from the top twice. */
    midTask(){ return dialogOpen() || filled('wo-customer') || filled('wo-service'); },
    steps: [
      { kind: 'intro',
        title: 'Let’s create a work order',
        body: '<p>You’re on the <b>Work Orders</b> tab. There are four things to do: pick a branch, ' +
              'open the form, fill in the two required fields, and submit.</p>' +
              '<p>I’ll point at each one. Do it for real — this is the screen, not a picture of it.</p>' },

      { target: '[data-tour="wo-branches"]', place: 'bottom',
        title: 'Pick the branch first',
        body: '<p>A work order belongs to one branch, and its number comes from that branch’s prefix. ' +
              'You cannot change it afterwards.</p>' +
              '<p>Look at the top right: on <span class="k">All</span> there is no <span class="k">+ Create</span> button at all. ' +
              'That is why this step comes first.</p>',
        /* No Skip: + Create does not exist until a branch is picked, so the
           step after this one cannot be done at all without it. */
        doIt: 'Click the branch the job is on',
        noSkip: true,
        done: () => branchPicked() || wtMadeOne() },

      { target: '[data-tour="wo-create"]', place: 'bottom',
        title: 'Now + Create is there',
        body: '<p>It appeared the moment a single branch was selected — along with ' +
              '<span class="k">Filter</span>.</p>',
        doIt: 'Click <b>+ Create</b>',
        noSkip: true,
        done: () => dialogOpen() || wtMadeOne() },

      { target: '[data-tour="wo-customer"]', also: '[data-tour="wo-customer"] ul', place: 'right', avoid: '[data-tour="wo-dialog"]',
        title: 'Choose the customer',
        body: '<p><b>Required</b> — the form will not submit without it. The customer has to exist in Makani ' +
              'already; if it does not, you have to add it from the Customers tab first and come back.</p>' +
              '<p>There are a lot of them, so type to narrow the list.</p>',
        doIt: 'Open the list and pick a customer',
        noSkip: true,
        /* Back from here means the step about opening the form, so the form
           has to be shut on the way — otherwise it sits there over a tile
           telling the reader to open it. Nothing is saved by cancelling. */
        onBack(){ closeWith('[data-tour="wo-cancel"]'); },
        done: () => filled('wo-customer') || wtMadeOne() },

      { target: '[data-tour="wo-service"]', also: '[data-tour="wo-service"] ul', place: 'right', avoid: '[data-tour="wo-dialog"]',
        title: 'Choose the type of service',
        body: '<p><b>Also required.</b> Service Call, Quoted Job, PM, Parts Only, Compressor Rebuild or Admin Hours.</p>' +
              '<p>If the one you need is missing, <span class="k">+ Add Service Type</span> beside the label adds it.</p>',
        doIt: 'Pick a type of service',
        noSkip: true,
        done: () => filled('wo-service') || wtMadeOne() },

      { target: '[data-tour="wo-optional-start"]', targetEnd: '[data-tour="wo-description"]', also: '[data-tour="wo-dialog"] ul',
        place: 'right', avoid: '[data-tour="wo-dialog"]', kind: 'info',
        title: 'The rest is optional',
        body: '<p>Everything between <b>NTE</b> and <b>Description</b> can be left exactly as it is. ' +
              'Fill in whatever you already know:</p>' +
              '<ul>' +
              '<li><b>NTE</b> — the not-to-exceed dollar amount. Defaults to $0.</li>' +
              '<li><b>Start Date</b> — defaults to today.</li>' +
              '<li><b>Asset</b> — the unit being worked on.</li>' +
              '<li><b>Customer Purchase Order #</b> — the customer’s PO number, if they gave you one.</li>' +
              '<li><b>Assign Primary Technician</b> — who is taking the job. Safe to leave blank now and ' +
                  'set later, though the job does not reach anyone until someone is assigned.</li>' +
              '<li><b>Description</b> — what is wrong.</li>' +
              '</ul>',
        done: () => wtMadeOne() },

      { target: '[data-tour="wo-description"]', place: 'right', avoid: '[data-tour="wo-dialog"]', kind: 'info',
        title: 'One of them is worth your time',
        body: '<p>The description is what the technician reads first, so be specific.</p>' +
              '<p>“Walk-in freezer running at 30 degrees” beats “freezer issue”.</p>',
        done: () => wtMadeOne() },

      /* The one step in the Knowledge Base that makes something real. The dim
         stays over SUBMIT until the reader says they meant to: until then the
         step lights nothing, so the button cannot be clicked by accident. Yes
         lights SUBMIT; No lights CANCEL and the tour ends on the other finish. */
      { target: () => (askedYes() ? '[data-tour="wo-submit"]' : askedNo() ? '[data-tour="wo-cancel"]' : null),
        lookAt: '[data-tour="wo-dialog"]', place: 'right', avoid: '[data-tour="wo-dialog"]',
        /* no way past the card but through it */
        noSkip: true, noNext: () => !askedYes() && !askedNo(),
        prep(){ resetAsk(); },
        ask: { target: '[data-tour="wo-submit"]', place: 'top', flag: 'Real work order',
          title: 'This one is not a rehearsal',
          body: '<p>Clicking <b>SUBMIT</b> creates a work order your branch will see, with a number of its ' +
                'own. Only go on if you actually want one.</p>',
          yes: 'Yes, create it', no: 'No, not now' },
        title: () => (askedNo() ? 'Cancel it instead' : 'Submit'),
        body: () => (askedNo()
          ? '<p>Nothing you have typed is saved, and no work order is made. <b>CANCEL</b> is lit; ' +
            '<b>SUBMIT</b> stays covered.</p>'
          : '<p>Notice it was greyed out until both required fields were set. That is the only thing ' +
            'stopping you — nothing else on the form is checked.</p>' +
            (askedYes() ? '' : '<p>Answer the note beside the button to carry on.</p>')),
        doIt: () => (askedNo() ? 'Click <b>CANCEL</b>' : askedYes() ? 'Click <b>SUBMIT</b>' : null),
        done: () => wtMadeOne() || (askedNo() && !dialogOpen()) },

      { kind: 'finish', when: () => !askedNo(), place: 'bottom',
        /* The row that was just created, by id, wherever the list puts it — the
           create succeeds before the list refetches, so for a moment the row is
           not there at all; until it is, the card sits centred with no ring, and
           the watcher lights the row the instant it appears. */
        target: () => (woLastCreatedId() ? '#wo-row-' + woLastCreatedId() : '[data-tour="wo-row"]'),
        /* The table scrolls sideways and Status is the column that falls off —
           the one thing this card is about. Bring it into view first. */
        prep(){
          const q = woLastCreatedId() ? '#wo-row-' + woLastCreatedId() : '[data-tour="wo-row"]';
          glideTo(q);
          const row = document.querySelector(q);
          let p = row && row.parentElement;
          while (p && p !== document.body){ if (p.scrollWidth > p.clientWidth + 2){ p.scrollLeft = p.scrollWidth; break; } p = p.parentElement; }
        },
        title: 'That’s a work order',
        body: '<p>It is at the top of the list in <b>Open</b> status — not Requested — with its number ' +
              'assigned from the branch prefix.</p>' +
              '<p>It has no History yet: creating a work order is not itself logged. That is normal.</p>' },

      /* The other ending: the reader said no and cancelled. Nothing was made,
         so there is no row of theirs to point at — the top of the list is
         where it would have gone, and that is worth showing. */
      { kind: 'finish', when: askedNo, place: 'bottom', target: '[data-tour="wo-row"]',
        prep(){
          glideTo('[data-tour="wo-row"]');
        },
        title: 'Nothing was created',
        body: '<p>You cancelled, so no work order was made and nothing you typed was saved.</p>' +
              '<p>Had you submitted it, it would have appeared right here at the top of the list — in ' +
              '<b>Open</b> status, with its number taken from the branch prefix.</p>' }
    ]
  },

  'find-work-orders': {
    label: 'Find a work order',
    /* Nothing here is written to the database, and nothing is navigated: the
       tour opens where the reader already is and asks which way they want to
       go, so the fork is their choice rather than one made for them by a
       redirect. Reset only forgets that choice. */
    reset(){
      seen.search = false;
      clearNavPick();
    },
    midTask(){ return searching() || filterOpen() || filtered() || onCustomerPath(); },
    /* Opening a work order is the whole point of this one: whichever step the
       reader is on, that is the end of it. */
    finishWhen: () => onWorkBranch() && onDetail(),
    /* every page is home: this one starts from wherever the reader is */
    onHome: () => true,
    /* No pauseWhen. Opening a work order used to fold the tour into the
       header badge; it now finishes on the work order instead — the reader
       got what they came for, and a tour about finding a job should say so
       rather than get out of the way. Every step from "Over to you" on lets
       onDetail() end it, so the finish is reached from wherever they were. */
    steps: [
      { kind: 'intro',
        title: 'Let’s find a work order',
        body: '<p>There are two places to look for a job: the <b>Work Orders</b> tab, or the <b>Customers</b> ' +
              'tab if you know the customer it belongs to.</p>' +
              '<p>I’ll follow whichever you pick — you don’t have to be on either one now.</p>' },

      /* The fork. It opens on whatever screen the reader is on and points at
         both tabs; the one they click chooses the branch (navPick), and there
         is deliberately no Next or Skip, so the tour never picks for them. */
      { target: '[data-tour="nav-work"]', extra: '[data-tour="nav-customers"]',
        place: 'right', placeOnMain: true, noSkip: true,
        prep(){ clearNavPick(); },
        title: 'Which way would you like to look?',
        body: '<p>Pick <b>Work Orders</b> to search every job at once — by number, customer, or a word ' +
              'from the description.</p>' +
              '<p>Or pick <b>Customers</b> if you know whose job it is: a customer’s site lists its own ' +
              'work orders.</p>',
        doIt: 'Click <b>Work Orders</b> or <b>Customers</b>',
        done: () => !!navPick() },

      /* The four ways of narrowing the list, one tile at a time: the tile
         moves to each control in turn, the control lights up, and Next moves
         it on. The dim's holes fade between them (fade) rather than snapping,
         which is the only thing left of the version that played itself. */
      { when: onWorkBranch, kind: 'info', goOn: true, fade: true, center: false,
        prep(){ woScroll(null); },
        body: '<p>There are a few ways to narrow down the work orders in this tab.</p>',
        done: () => onDetail() },
      /* done() on every one of them: opening a work order is the end of this
         walkthrough wherever the reader is when they manage it. */
      ...WO_WAYS.map(w => ({ when: onWorkBranch, kind: 'info', goOn: true, fade: true, done: () => onDetail(), ...w })),
      /* and then out of the way, with everything the reader needs left open.
         Narrow, so it sits inside the app's own menu rather than hanging over
         the list it has just handed back. */
      { when: onWorkBranch, kind: 'info', noNext: true, fade: true, center: false, narrow: true, noDots: true,
        dock: 'left-mid', also: '[data-tour="wo-search"] ul',
        /* the two headings are ringed inside the lit table, so it is plain
           there is something to point at */
        /* the search box grows with its suggestions rather than being covered
           by them — its own list is part of it, not something lying over it */
        outline: ['[data-tour="wo-table"]',
          { target: '[data-tour="wo-search"]', also: '[data-tour="wo-search"] ul' },
          '[data-tour="wo-branches"]',
          '[data-tour="wo-sort-number"]', '[data-tour="wo-sort-date"]'],
        prep(){ woScroll(null); markBranch(); },
        /* Hover to discover: the tile says what to do, and pointing at any of
           the lit controls says what that one is for. Cards take no clicks, so
           nothing under them is blocked. */
        hovers: true,
        callouts: [
          { target: '[data-tour="wo-search"]', place: 'bottom', hover: true, title: 'Search',
            body: '<p>A work order number, a customer, or a word from the description — the list narrows as you type.</p>',
            hideWhen: () => !hovering('[data-tour="wo-search"]') },
          { target: '[data-tour="wo-branches"]', place: 'bottom-left', hover: true, title: 'Branches',
            body: '<p>Pick a branch and the list is cut to it — and <span class="k">Filter</span> appears, for narrowing it further by status, customer, technician or dates.</p>',
            hideWhen: () => !hovering('[data-tour="wo-branches"]') },
          { target: '[data-tour="wo-sort-number"]', place: 'bottom-left', hover: true, title: 'WO#',
            body: '<p>Click the heading to sort by work order number, and again to turn the order round.</p>',
            hideWhen: () => !hovering('[data-tour="wo-sort-number"]') },
          { target: '[data-tour="wo-sort-date"]', place: 'bottom', hover: true, title: 'Date Started',
            body: '<p>The only other heading that sorts — newest jobs first, or oldest.</p>',
            hideWhen: () => !hovering('[data-tour="wo-sort-date"]') }
        ],
        title: 'Over to you',
        body: '<p>Try one: narrow the list down, then click the work order you are after.</p>' +
              '<p>Point at any of the highlighted parts to see what it does.</p>',
        /* Opening a work order is what all of this was for: from here on every
           step lets that end it, so a reader who finds their job at any point
           lands on the finish rather than being walked through the filter.
           Picking a branch moves them on as well — the next tiles are about
           the Filter button that has just appeared because of it. */
        done: () => filterOpen() || filtered() || onDetail() || branchSince() },

      /* Filter is not on the page at all until a branch is picked. A reader
         who already picked one never sees this step: done() is true when they
         arrive, and the watcher carries them straight past it. */
      { when: onWorkBranch, target: '[data-tour="wo-branches"]', place: 'bottom',
        title: 'Filter needs a branch',
        body: '<p>Look at the top right: on <span class="k">All</span> there is no <span class="k">Filter</span> button. ' +
              'Filtering only works inside a single branch.</p>',
        /* without a branch there is no Filter button for the next step to point at */
        noSkip: true,
        doIt: 'Click a branch tab',
        done: () => branchPicked() || filterOpen() || filtered() || onDetail() },

      { when: onWorkBranch, id: 'filter-here', target: '[data-tour="wo-filter"]', place: 'bottom',
        title: 'Now Filter is there',
        body: '<p>It appeared with the branch — and so did <span class="k">Download</span>, which exports ' +
              'whatever the filter finds as an Excel file.</p>',
        /* the next step is the panel this opens */
        noSkip: true,
        doIt: 'Click <b>Filter</b>',
        done: () => filterOpen() || filtered() || onDetail() },

      /* the five dropdowns open a <ul> inside the panel that hangs below it;
         the two date fields open a full-screen date picker instead */
      { when: onWorkBranch, target: '[data-tour="wo-filter-panel"]', also: '[data-tour="wo-filter-panel"] ul', yieldTo: '[data-tour="date-picker"]',
        place: 'bottom', kind: 'info',
        /* the panel opened to get here; Filter is a toggle, so it shuts it */
        onBack(){ if (filterOpen()) closeWith('[data-tour="wo-filter"]'); },
        title: 'Seven ways to narrow it',
        body: '<p><b>Service Type</b>, <b>Customer</b>, <b>Status</b>, <b>Technician</b>, <b>Start date</b>, ' +
              '<b>End date</b> and <b>Quoted by</b>. Any combination.</p>' +
              '<p>Set one now — <b>by Status</b> is the quickest to try.</p>',
        done: () => filtered() || onDetail() },

      { when: onWorkBranch, target: '[data-tour="wo-filter-apply"]', place: 'left',
        title: 'Apply',
        body: '<p>Nothing changes until you click it. The filter is written into the page address, so the ' +
              'browser’s Back button undoes it and a reload keeps it.</p>',
        /* the next tile says "that's the filtered list" — it has to be one */
        noSkip: true,
        doIt: 'Click <b>Apply</b>',
        done: () => filtered() || onDetail() },

      /* Handed back, the same way step 8 is: the list and Clear are both
         open. It used to light Clear alone, which asked the reader to pick a
         work order while the dim was lying over every one of them. */
      { when: onWorkBranch, noNext: true, fade: true, center: false, narrow: true, noDots: true,
        dock: 'left-mid',
        outline: ['[data-tour="wo-table"]', '[data-tour="wo-filter-clear"]'],
        title: 'That’s the filtered list',
        body: '<p>Click the work order you were after — or keep narrowing.</p>' +
              '<p><b>Clear</b> resets every field at once; the filter otherwise stays on as you move between ' +
              'branches. And the list loads in batches, so try <b>LOAD MORE</b> at the bottom before deciding ' +
              'a job is missing.</p>',
        done: () => onDetail() },

      /* The end of a tour about finding a job is the job. No dim: the point is
         to look at the page the reader has just reached. */
      { when: onWorkBranch, kind: 'finish', view: true,
        title: 'That’s the one',
        body: '<p>Here is the work order you went looking for — everything about the job lives on this page.</p>' +
              '<p>Any of the ways you have just seen will bring you back to it, and the search box at the top ' +
              'works from every screen in Makani.</p>' },

      /* The customer branch: Customers → the customer → one of its sites,
         whose page lists that site's work orders. Only these three run for a
         reader who took that route, and the count reads 3 of 5, not 3 of 12. */
      /* The lit area is the whole card, table and pager together: ten customers
         to a page, and the one they want may be on the next. The header search
         is lit alongside it, with a callout of its own. */
      /* The list fills the page, so the tile has nowhere to go beside it: it
         sits narrow over the app's own menu, level with the top of the lit
         card, clear of both the customers and the pager. */
      { when: onCustomerBranch, target: '[data-tour="cust-list"]', dock: 'left-top', narrow: true,
        placeOnMain: true, noSkip: true, popIn: true,
        extra: '[data-tour="wo-search"]', extraAlso: '[data-tour="wo-search"] ul',
        title: 'Pick the customer',
        body: '<p>Click the customer’s <b>name</b> — the blue text — to open them. The rest of the row does ' +
              'nothing, and the arrow only unfolds their sites underneath.</p>' +
              '<p>They are listed ten to a page; if yours is not here, the pager at the bottom has the rest.</p>',
        callouts: [
          { target: '[data-tour="wo-search"]', place: 'bottom', title: 'Or search by name',
            body: '<p>Type the customer’s name up here to narrow the list.</p>',
            /* it sits exactly where the suggestions drop */
            hideWhen: () => !!document.querySelector('[data-tour="wo-search"] ul') }],
        doIt: 'Click the customer’s name',
        done: () => onCustomerAccount() || onLocationPage() },

      { when: onCustomerBranch, target: '[data-tour="cust-locations"]', place: 'top',
        /* Back means the step before, which is the list of customers — and
           that is a different page, so going back has to take the reader
           there or the tile ends up describing a screen they are not on. */
        onBack(navigate){ navigate('/customer'); },
        title: 'Now the site',
        body: '<p>A customer can have any number of sites, and this is where the branch comes in: each site ' +
              'belongs to one, shown in the <b>Branch</b> column.</p>' +
              '<p>Work orders hang off the site, not the customer, so open the one the job is at.</p>',
        /* the finish tile lives on the site's page */
        noSkip: true,
        doIt: 'Click a site',
        done: () => onLocationPage() },

      /* The list is as long as the site is busy — one job at a quiet site,
         dozens at a busy one — so rather than counting them, light the column
         of work order numbers: that is the part that is clickable. */
      { when: onCustomerBranch, kind: 'finish', target: '[data-tour="loc-wos"]', place: 'top',
        outline: ['all:[data-tour="loc-wo-number"]'],
        prep(){ glideTo('[data-tour="loc-wos"]'); },
        title: 'That site’s work orders',
        body: '<p>Every job at this site is listed here with its description, branch and status.</p>' +
              '<p>The numbers down the left are the links: clicking one opens that work order in a ' +
              '<b>new tab</b>, so this page stays where it is.</p>' }
    ]
  },

  'assign-technicians': {
    label: 'Assign a technician',
    /* The first three steps find the job, so the reader never has to leave
       the walkthrough to go looking for it. Started from a work order that is
       already open (the drawer), midTask() is true and the landing skips
       straight to that job's Assigned To. */
    onHome: () => onWorkList() || onDetail(),
    begin(){ techMark = techsSavedCount(); teamAtOpen = null; },
    reset(navigate){ navigate('/work?branch=0', { replace: true }); },
    midTask(){ return onDetail() || assignOpen(); },
    steps: [
      { kind: 'intro',
        title: 'Let’s put someone on a job',
        body: '<p>Three things: find the work order, open it, and add the person in the <b>Assign</b> window.</p>' +
              '<p>I’ll go with you the whole way, starting with finding the job.</p>' },

      findJobStep(),
      viewJobStep('assign a technician', () => assignOpen() || savedTechs()),

      /* Requested jobs stop here. There is no + Add to point at until the
         request is approved, and the step after this one asks the reader to
         click it — so say what is going on, show them where the button will
         appear, and end the walkthrough cleanly instead of stranding them. */
      { when: jobRequested, kind: 'stop', target: '[data-testid="wo-status-badge"]', place: 'bottom',
        outline: ['[data-tour="wo-assigned"]'], popIn: true,
        callouts: [
          { target: '[data-tour="wo-assigned"]', place: 'left', title: 'Nothing to click yet',
            body: '<p>Once the request is approved and the status changes, the <span class="k">+ Add</span> ' +
                  'button you use to assign a technician appears here.</p>' }],
        title: 'This one is still Requested',
        body: '<p>A work order stays <b>Requested</b> until someone approves it, and a request cannot be ' +
              'assigned to anyone — that is why there is no button beside <b>Assigned To</b>.</p>' +
              '<p>Approve it first, then start this walkthrough again on the same job.</p>' },

      { when: () => !jobRequested(), target: '[data-tour="wo-assigned"]', place: 'left', noSkip: true, popIn: true,
        /* The reader may have been reading anywhere down the page on the step
           before; bring Assigned To into the middle first, then let the tile
           and the ring pop in where they belong. */
        prep(){ glideTo('[data-tour="wo-assigned"]'); },
        title: 'Assigned To',
        /* the button is not rendered on a Requested job, or without
           wo:assignTech — say so rather than point at nothing */
        body: () => document.querySelector('[data-tour="wo-assign-open"]')
          ? '<p>Everyone on the job is listed here. The button underneath reads <span class="k">Edit</span> ' +
            'once someone is assigned, and <span class="k">+ Add</span> while nobody is.</p>' +
            '<p>Not the pencil icon — that opens a different form, which does not handle technicians.</p>'
          : '<p>There is no button here, so this job cannot be assigned: your role does not include ' +
            'assigning technicians.</p>',
        doIt: 'Click <b>Edit</b> or <b>+ Add</b>',
        done: () => assignOpen() || savedTechs() },

      { target: '[data-tour="assign-rows"]', also: '[data-tour="assign-menu"]',
        place: 'right', avoid: '[data-tour="wo-assign-dialog"]',
        /* what the rows held when the window opened, so "picked someone"
           means someone new — not a name that was already there */
        prep(){ teamAtOpen = assignRows().map(r => r.u); },
        /* the window opened to get here, so Back shuts it again */
        onBack(){ closeWith('[data-tour="assign-cancel"]'); },
        title: 'Pick the person',
        body: '<p>Each row is one person. Open <b>Team</b> and choose them — type to narrow the list.</p>' +
              '<p>Everyone already on the job has a row. To add someone alongside them, click ' +
              '<span class="k">+ Add more</span> below for an empty row first.</p>',
        doIt: 'Choose someone under <b>Team</b>',
        /* skipping it is skipping the point of the walkthrough */
        noSkip: true,
        done: () => teamPicked() || savedTechs() },

      { target: '[data-tour="assign-rows"]', also: '[data-tour="assign-menu"]',
        place: 'right', avoid: '[data-tour="wo-assign-dialog"]',
        title: 'Give them a role',
        body: '<p><b>Primary technician</b> is the lead — the name shown in the list, and on purchase ' +
              'order emails. <b>Technician</b> is anyone else on the job.</p>' +
              '<p>Keep it to one Primary unless you mean it. Nothing stops you choosing two.</p>' +
              '<p><b>SAVE stays greyed out</b> until every row with somebody in it also has a role — ' +
              'so there is no getting past this one, here or at the end.</p>',
        doIt: 'Pick a <b>Role</b>',
        /* Not skippable, and deliberately so. A row with a person and no role
           is the one thing that greys SAVE out, and letting the reader skip
           past it meant the SAVE step had to carry a second face explaining
           the mess and offering to light the offending row — a whole fork of
           the walkthrough to describe a state the walkthrough itself created.
           Holding them here costs one click and removes all of it. */
        noSkip: true,
        done: () => (teamPicked() && assignRows().every(r => r.u === '-1' || r.r !== '-1')) || savedTechs() },

      /* The lit area is the rows and the link together, so it grows downward
         as rows are added instead of chasing the link down the window — which
         let the reader add row after row without ever reaching one. */
      { target: '[data-tour="assign-rows"]', targetEnd: '[data-tour="assign-add-more"]',
        also: '[data-tour="assign-menu"]',
        place: 'right', avoid: '[data-tour="wo-assign-dialog"]', kind: 'info',
        title: 'More than one person?',
        body: '<p><span class="k">+ Add more</span> adds another row — fill it in the same way, ' +
              '<b>Team</b> then <b>Role</b>. The trash icon at the end of a row takes that person off the ' +
              'job when you save.</p>',
        done: () => savedTechs() },

      /* One tile, because by the time the reader is here every row already has
         a role: the role step will not let them past without one. This step
         used to have a second face for the other case, lighting the offending
         row — and that face was only ever reachable through its own Skip
         button, which has gone. */
      { target: '[data-tour="assign-save"]', place: 'right', avoid: '[data-tour="wo-assign-dialog"]',
        also: '[data-tour="assign-menu"]', noSkip: true,
        title: 'Save',
        body: '<p>It stays greyed out while any row has a person but no role — that is the only check ' +
              'there is. Add a row of your own at any point and the same rule applies to it.</p>',
        doIt: 'Click <b>SAVE</b>',
        done: () => savedTechs() },

      { kind: 'finish', target: '[data-tour="wo-assigned"]', place: 'left',
        title: 'They’re on the job',
        body: '<p>The names under <b>Assigned To</b> are who has it now. The icons beside each one are set ' +
              'from the technician app — the article explains what each means.</p>' +
              '<p>To change anyone later, come back to <span class="k">Edit</span> here.</p>' }
    ]
  },

  'edit-work-order': {
    label: 'Edit a work order',
    /* Same opening as assign: find the job, say what the page is. The window
       is the create dialog in edit mode, so its field anchors are shared with
       the create walkthrough; wo-status and wo-number exist only here. */
    onHome: () => onWorkList() || onDetail(),
    begin(){ editMark = woEditedCount(); },
    reset(navigate){ navigate('/work?branch=0', { replace: true }); },
    midTask(){ return onDetail(); },
    steps: [
      { kind: 'intro',
        title: 'Let’s edit a work order',
        body: '<p>Three things: find the work order, open it, and make your changes in the ' +
              '<b>Edit Work Order</b> window.</p>' +
              '<p>I’ll go with you the whole way, starting with finding the job.</p>' },

      findJobStep(),
      viewJobStep('make your changes', () => editOpen() || savedEdit()),

      { target: '[data-tour="wo-edit-open"]', place: 'bottom',
        /* the reader may have read their way down the job on the step before */
        prep(){ glideTo('[data-tour="wo-edit-open"]'); },
        title: 'The pencil',
        /* rendered only with the wo:update permission */
        body: () => document.querySelector('[data-tour="wo-edit-open"]')
          ? '<p>The pencil at the top right of the service panel, beside ' +
            '<span class="k">SERVICE TICKET</span>, opens the <b>Edit Work Order</b> window.</p>'
          : '<p>There is no pencil here, so your role does not include editing work orders.</p>',
        doIt: 'Click the pencil',
        /* the step after this one is inside the window it opens */
        noSkip: true,
        done: () => editOpen() || savedEdit() },

      /* the status list is the app's own <ul>, hanging below the dropdown */
      { target: '[data-tour="wo-status"]', also: '[data-tour="wo-status"] ul',
        place: 'right', avoid: '[data-tour="wo-dialog"]', kind: 'info',
        /* the pencil opened this window on the step before; Back shuts it */
        onBack(){ closeWith('[data-tour="wo-cancel"]'); },
        title: 'Status is up here',
        body: '<p>The <b>Status</b> dropdown sits in the top right corner of the window — easy to miss.</p>' +
              '<p>Any status can be picked at any time; nothing makes a job go through them in order.</p>',
        done: () => savedEdit() },

      { target: '[data-tour="wo-number"]', place: 'right', avoid: '[data-tour="wo-dialog"]', kind: 'info',
        title: 'What this window cannot change',
        body: '<p>The <b>work order number</b> is fixed for the life of the job, and so is its branch — ' +
              'the number is built from the branch prefix.</p>' +
              '<p>Technicians are not in here either. They are changed from <span class="k">Edit</span> under ' +
              '<b>Assigned To</b> on the work order page.</p>',
        done: () => savedEdit() },

      { target: '[data-tour="wo-customer"]', targetEnd: '[data-tour="wo-description"]', also: '[data-tour="wo-dialog"] ul',
        place: 'right', avoid: '[data-tour="wo-dialog"]', kind: 'info',
        title: 'Change what you need',
        body: '<p><b>Customer</b> and <b>Type of Service</b> are required, as they were when the job was created.</p>' +
              '<p>The rest can be changed or left as they are: <b>NTE</b>, <b>Start Date</b>, <b>Asset</b>, ' +
              '<b>Customer Purchase Order #</b> and <b>Description</b>.</p>',
        done: () => savedEdit() },

      { target: '[data-tour="wo-submit"]', place: 'right', avoid: '[data-tour="wo-dialog"]',
        title: 'Submit',
        /* skipping it would put the finish up over an unsaved, still-open form */
        noSkip: true,
        body: '<p>Nothing is saved until you click it. <span class="k">CANCEL</span> closes the window ' +
              'without saving.</p>',
        doIt: 'Click <b>SUBMIT</b>',
        done: () => savedEdit() },

      { kind: 'finish', target: '[data-testid="wo-status-badge"]', place: 'bottom',
        title: 'Saved',
        body: '<p>The service panel now shows the new details. A change of status is also recorded in ' +
              '<b>History</b> at the bottom of the page, with who made it and when.</p>' +
              '<p>You can come back to the pencil as often as you need — it does not lock, even on a Billed job.</p>' }
    ]
  },

  'work-order-service-ticket': {
    label: 'Preview a service ticket',
    /* Same opening as assign / edit / documents. On a Completed job every
       preview step is "done" at once (completedJob), so the tour runs
       straight to the finish, which says where the editing page is. */
    onHome: () => onWorkList() || onDetail(),
    reset(navigate){ navigate('/work?branch=0', { replace: true }); },
    midTask(){ return onDetail(); },
    steps: [
      { kind: 'intro',
        title: 'Let’s look at a service ticket',
        body: '<p>The service ticket is the customer’s paperwork for the job, built from the work order.</p>' +
              '<p>Find the work order, and I’ll show you where it is generated.</p>' },

      findJobStep(),
      viewJobStep('see its service ticket', () => stPreviewOpen() || completedJob()),

      { target: '[data-tour="wo-st-preview"]', place: 'bottom', id: 'st-button',
        /* the reader may have read their way down the job on the step before */
        prep(){ glideTo('[data-tour="wo-st-preview"]'); },
        title: 'SERVICE TICKET',
        body: '<p>Top right of the service panel. It builds the ticket as a PDF from what is on the work ' +
              'order right now — it takes a couple of seconds.</p>',
        noSkip: true,
        doIt: 'Click <b>SERVICE TICKET</b>',
        done: () => stPreviewOpen() || completedJob() },

      { target: '[data-tour="st-viewonly"]', place: 'bottom', avoid: '[data-tour="st-actions"]', kind: 'info',
        /* SERVICE TICKET opened this preview on the step before */
        onBack(){ closeWith('[data-tour="st-close"]'); },
        title: 'A preview, not the finished ticket',
        body: '<p>Until the job is <b>Completed</b> the ticket can only be looked at. Once it is, the same ' +
              'button opens the service ticket page, where it can be edited and sent.</p>' +
              '<p>Check the top-left corner of the PDF: the letterhead comes from the branch, and a branch ' +
              'without one prints an empty logo box and no company details.</p>',
        done: () => completedJob() },

      { target: '[data-tour="st-actions"]', place: 'bottom', kind: 'info',
        title: 'Download or print it',
        body: '<p><span class="k">Download PDF</span> saves a copy; <span class="k">Print</span> sends it to ' +
              'your printer. The viewer below also has its own zoom and page controls.</p>',
        done: () => completedJob() },

      { kind: 'finish', place: 'left',
        target: () => (completedJob() ? '[data-tour="wo-st-open"]' : '[data-tour="st-close"]'),
        /* A Completed job has no preview: the two steps behind this one were
           satisfied the moment the tour reached them and the reader never saw
           them, so Back goes to the button instead of a window that never
           opened. With a preview open, Back is simply back. */
        backTo: () => (completedJob() ? 'st-button' : null),
        title: 'That’s the service ticket',
        body: () => (completedJob()
          ? '<p>This job is <b>Completed</b>, so <span class="k">SERVICE TICKET</span> opens the service ticket ' +
            'page instead of a preview — where the ticket is edited and sent to the customer.</p>'
          : '<p>Close it with the ✕. Two things change what prints: a photo appears only if its ' +
            '<b>Include In Service Ticket</b> toggle is on, and the technician hours come from the ' +
            'work order’s timesheet.</p>') }
    ]
  },

  'create-po-from-work-order': {
    label: 'Raise a purchase order',
    /* A purchase order has to hang off a job, so this begins where the other
       work-order tours begin: find the work order the parts are for. The
       Purchase Orders tab is deliberately not a starting point — it has no
       create button at all, which is the first thing the article says. */
    onHome: () => onWorkList() || onDetail(),
    begin(){ poMark = poCreatedCount(); resetAsk(); },
    reset(navigate){ navigate('/work?branch=0', { replace: true }); },
    midTask(){ return onDetail() || poOpen(); },
    steps: [
      { kind: 'intro',
        title: 'Let’s raise a purchase order',
        body: '<p>Every purchase order in Makani belongs to a work order — which is why the ' +
              '<b>Purchase Orders</b> tab has no <b>+ Create</b> button on it at all.</p>' +
              '<p>So we start from the job the parts are for.</p>' },

      findJobStep(),
      viewJobStep('raise a purchase order', () => poOpen() || poMade()),

      { target: '[data-tour="po-create"]', place: 'bottom', noSkip: true,
        /* The reader may have been reading their way down the job on the step
           before this one, and this button lives at the very top of it. Go
           back up before saying anything, or the tile arrives pointing at a
           button that is off the screen. */
        prep(){ glideTop('[data-tour="po-create"]'); },
        title: 'Create PO',
        body: '<p>Top right of the work order, level with the <b>Back</b> link — one row below the ' +
              '<b>Work Order #</b> heading.</p>',
        /* no po:create: the button is not rendered at all */
        absent: {
          title: 'No Create PO button',
          body: '<p>Raising a purchase order needs the <b>Create Purchase Orders</b> permission, and your ' +
                'role does not include it.</p>' +
                '<p>Purchase orders already on this job still show in the <b>Purchase Orders</b> section ' +
                'further down the page.</p>' },
        doIt: 'Click <b>+ Create PO</b>',
        done: () => poOpen() || poMade() },

      /* Simple only, as agreed: Detailed is PO-02's subject. The callout says
         it exists and where it is covered, rather than pretending it does not. */
      /* The fork. Whichever the reader picks, the rest of the tour follows —
         and it follows them again if they change their mind later, because
         the steps are chosen by what the window is showing, not by what was
         clicked here. The two routes rejoin at Issued For. */
      { target: '[data-tour="po-mode"]', place: 'right', avoid: '[data-tour="po-dialog"]',
        kind: 'info', goOn: true,
        /* the window opened to get here; nothing is saved until SUBMIT */
        onBack(){ closeWith('[data-tour="po-cancel"]'); },
        /* the window scrolls, and Back from any later step arrives with it
           wherever that step left it — see the note on po-wonumber */
        prep(){ glideTo('[data-tour="po-mode"]'); },
        title: 'Simple or Detailed',
        body: '<p>This is the one choice that changes the rest of the form.</p>' +
              '<p><b>Simple</b> describes the order in plain words. <b>Detailed</b> itemises it instead ' +
              '— part by part, with quantities and pricing, and a total.</p>' +
              '<p>Pick either and I’ll follow. You can change your mind before you submit, though ' +
              'switching away from Detailed clears the line items you have entered.</p>',
        done: () => poMade() },

      { target: '[data-tour="po-wonumber"]', place: 'right', avoid: '[data-tour="po-dialog"]',
        kind: 'info',
        /* The Create PO window has its own scroller, and the steps further
           down it (Quote Number, Note, SUBMIT) scroll it to get there. Coming
           back up, this step used to say nothing about where it wanted the
           form — so the ring was drawn on a control that had scrolled away
           and clipped to a sliver at the top of the window. Every step in
           this window brings its own control back into view. */
        prep(){ glideTo('[data-tour="po-wonumber"]'); },
        title: 'The job is already filled in',
        body: '<p>The work order number is greyed out because it is not yours to change — it is the job ' +
              'you opened this from.</p>' +
              '<p>If it is the wrong job, cancel and start again from the right work order.</p>',
        done: () => poMade() },

      /* Three fields at once, with the trap kept out of the way on a hover
         card: the form is long and the tile has one narrow column beside it. */
      { lookAt: '[data-tour="po-payment"]',
        place: 'right', avoid: '[data-tour="po-dialog"]',
        hovers: true, kind: 'info',
        prep(){ glideTo('[data-tour="po-payment"]'); },
        /* Each of the three opens a list of its own. The ring grows to take
           its list in while it is open, and gets out of the way while one of
           the others is covering it — see outlineParts. */
        outline: [
          { target: '[data-tour="po-payment"]', also: '[data-tour="po-payment-list"]' },
          { target: '[data-tour="po-vendor"]', also: '[data-tour="po-vendor-list"]' },
          { target: '[data-tour="po-issued"]', also: '[data-tour="po-issued-list"]' }],
        title: 'Who, and how it is paid for',
        body: '<p><b>Payment Type</b> is how the parts are being paid for — on the company’s account ' +
              'with that vendor, out of someone’s pocket, or on a company card.</p>' +
              '<p><b>Vendor</b> is who you are buying from, and <b>Issued For</b> is who the parts are ' +
              'for — usually the tech on the job.</p>' +
              '<p>Point at any of the three for what it does.</p>',
        callouts: [
          { target: '[data-tour="po-payment"]', place: 'right', hover: true, title: 'A label, not a route',
            body: '<p>All three print on the PDF and can be filtered on, but none of them sends the order ' +
                  'down a different approval path. <b>Neither this nor Vendor is required</b> — the ' +
                  'order will submit with both blank, and then nobody can tell who it went to.</p>',
            hideWhen: () => !hovering('[data-tour="po-payment"]') },
          { target: '[data-tour="po-vendor"]', place: 'right', hover: true, title: 'Vendor',
            body: '<p>Who the parts are being bought from. Not required either — fill it in anyway.</p>',
            hideWhen: () => !hovering('[data-tour="po-vendor"]') },
          { target: '[data-tour="po-issued"]', place: 'right', hover: true, title: 'Issued For',
            body: '<p>The person the parts are for, usually the technician on the job.</p>',
            hideWhen: () => !hovering('[data-tour="po-issued"]') }],
        done: () => poMade() },

      /* ---- the Simple route ---- */
      { when: poSimple, target: '[data-tour="po-description"]', place: 'right',
        avoid: '[data-tour="po-dialog"]', kind: 'info',
        prep(){ glideTo('[data-tour="po-description"]'); },
        title: 'Say what you are ordering',
        body: '<p>Plain words, and the more specific the better: “Run capacitor, 45/5 MFD” beats ' +
              '“parts”. This is what the vendor reads.</p>' +
              '<p>A Simple purchase order carries no prices and no total — it is a description and ' +
              'nothing else. If you need the money on it, that is Detailed.</p>',
        done: () => poMade() },

      /* ---- the Detailed route ---- */
      { when: poDetailed, target: '[data-tour="po-items"]', place: 'right',
        avoid: '[data-tour="po-dialog"]', kind: 'info',
        prep(){ glideTo('[data-tour="po-items"]', { align: 'top' }); },
        title: 'Itemise it',
        body: '<p><b>+ ADD</b> puts an empty line on the list. Each line takes what the part is, how many, ' +
              'and what it costs — and the lines add up to the order’s total.</p>' +
              '<p>Add as many as you need. The bin at the corner of a line takes it off again.</p>',
        done: () => poMade() },

      /* The money only exists on this route, so the NTE warning lives here
         rather than on a Simple order that has no amount at all. */
      { when: poDetailed, target: '[data-tour="po-items"]', place: 'right',
        avoid: '[data-tour="po-dialog"]', kind: 'info',
        prep(){ glideTo('[data-tour="po-items"]', { align: 'top' }); },
        title: 'Nothing checks this against the NTE',
        body: '<p>The job may carry a <b>not-to-exceed</b>. Nothing here looks at it.</p>' +
              '<p>A $500 order against a $100 NTE saves silently — no prompt, no flag, nothing in the ' +
              'history to say it happened. If that limit matters on this job, check it yourself before ' +
              'you submit.</p>',
        done: () => poMade() },

      { when: poDetailed, target: '[data-tour="po-quote"]', place: 'right',
        avoid: '[data-tour="po-dialog"]', kind: 'info',
        prep(){ glideTo('[data-tour="po-quote"]'); },
        title: 'Quote Number',
        body: '<p>If the vendor quoted this, <b>+ ADD</b> puts their quote number on the order. One only, ' +
              'and it is optional.</p>',
        done: () => poMade() },

      { target: '[data-tour="po-note"]', place: 'right', avoid: '[data-tour="po-dialog"]',
        kind: 'info',
        prep(){ glideTo('[data-tour="po-note"]'); },
        title: 'Note stays inside',
        body: '<p>Makani says so on the field itself: <i>for internal use, will not appear on PDF</i>.</p>' +
              '<p>So anything the vendor should not read goes here, not in <b>Description</b>.</p>',
        done: () => poMade() },

      /* SUBMIT makes a real purchase order AND moves the work order to Parts.
         The second one is a change to a record the reader did not touch, so
         the gate says it outright rather than leaving it to be discovered. */
      /* Until the question is answered there is no target at all, so no hole
         is cut and the dim keeps SUBMIT covered — a warning the reader can
         click straight past is not a gate. Answered yes, the hole opens on
         SUBMIT; answered no, it opens on CANCEL instead, because backing out
         is then the thing being asked for. */
      { target: () => (askedYes() ? '[data-tour="po-submit"]'
                     : askedNo() ? '[data-tour="po-cancel"]' : null),
        lookAt: '[data-tour="po-submit"]',
        place: 'right', avoid: '[data-tour="po-dialog"]',
        noSkip: true,
        prep(){ resetAsk(); glideTo('[data-tour="po-submit"]'); },
        noNext: () => !askedYes() && !askedNo(),
        ask: { target: '[data-tour="po-submit"]', place: 'right',
          flag: 'This one is real',
          title: 'Raise this purchase order?',
          body: '<p>There is no draft stage. It is created in <b>Issued</b> status straight away, and it ' +
                'shows on the job and in the Purchase Orders list.</p>' +
                '<p><b>It also moves this work order to Parts</b>, if this is its first purchase order. ' +
                'You do not choose that and you cannot stop it — it is recorded in the job’s ' +
                'History with no name against it.</p>',
          yes: 'Raise it', no: 'Not on a real job' },
        title: () => (askedNo() ? 'Back out of it' : 'Submit'),
        body: () => (askedNo()
          ? '<p>Then let’s not raise one. <b>CANCEL</b> is lit and <b>SUBMIT</b> stays covered — ' +
            'it shuts the window, nothing is created, and nothing you typed is kept.</p>' +
            '<p>The job’s status does not move either.</p>'
          : '<p>One click and the purchase order exists.</p>' +
            '<p>Worth a last look at what you are ordering before you do — that is the part the ' +
            'vendor reads.</p>' +
            (askedYes() ? '' : '<p>Answer the note beside the button to carry on.</p>')),
        doIt: () => (askedNo() ? 'Click <b>CANCEL</b>' : askedYes() ? 'Click <b>SUBMIT</b>' : null),
        /* Answering "no" and then submitting anyway is still a purchase order,
           and the finish has to be the one that says so. */
        done: () => poMade() || (askedNo() && !poOpen()) },

      /* The PO lands in the job's own Purchase Orders section, which is shut
         like every other section on this page. */
      { kind: 'finish', when: () => poMade() || !askedNo(), target: '[data-tour="wo-sec-pos"]', place: 'left',
        prep(){ glideTo('[data-tour="wo-sec-pos"]'); },
        title: 'That’s the purchase order',
        body: '<p>It is in <b>Purchase Orders</b> on this job — open the section to see it — and in ' +
              'the main Purchase Orders list with this job in the <b>Parent WO#</b> column.</p>' +
              '<p>Check the job’s status at the top. If this was the job’s <b>first</b> purchase ' +
              'order it has moved itself to <b>Parts</b>, recorded in History with no name against it.</p>' +
              '<p>That happens once and once only. Makani looks at whether the job has any purchase ' +
              'orders, not at what status it is in — so a second order never moves it, and neither ' +
              'does the first one again if somebody sets the job back to <b>Open</b> in between.</p>' },

      { kind: 'finish', when: () => askedNo() && !poMade(), target: '[data-tour="po-create"]', place: 'bottom',
        title: 'Nothing was created',
        body: '<p>You cancelled, so no purchase order was made and the job’s status has not moved.</p>' +
              '<p>The button is still there when you want it — and it is the only way in, on this job ' +
              'or any other.</p>' }
    ]
  },

  'work-order-timesheet': {
    label: 'Read the timesheet',
    /* Same opening as the other work-order tours. The timesheet is low down the
       right-hand column and the section is shut when the page loads, so the
       tour's own first job is getting it open and on screen. */
    onHome: () => onWorkList() || onDetail(),
    begin(){ resetAsk(); },
    reset(navigate){ navigate('/work?branch=0', { replace: true }); },
    midTask(){ return onDetail(); },
    steps: [
      { kind: 'intro',
        title: 'Let’s read the labour hours',
        body: '<p>Every work order keeps a <b>Timesheet</b>: who worked on the job, and for how long.</p>' +
              '<p>Find the work order and I’ll show you how to read it — and where the hours are ' +
              'entered, which is the part people miss.</p>' },

      findJobStep(),
      viewJobStep('read its hours', () => tsOpen()),

      /* The section is shut on arrival, so opening it is a real step rather
         than something the tour can assume. */
      { target: '[data-tour="wo-timesheet-head"]', place: 'left', noSkip: true,
        prep(){ glideTo('[data-tour="wo-timesheet"]'); },
        title: 'Open the Timesheet',
        body: '<p>It is in the right-hand column, near the bottom, and it starts closed — which is the ' +
              'first reason people miss it.</p>' +
              '<p>Click anywhere on the bar to open it.</p>',
        doIt: 'Click <b>Timesheet</b>',
        done: () => tsOpen() },

      /* The rows have their own 300px scroller inside the page, so arriving
         here moves two of them: the page, and the list. */
      { target: '[data-tour="ts-rows"]', extra: '[data-tour="ts-cols"]', placeOnMain: true,
        place: 'left', kind: 'info',
        /* the step behind this one is the section still shut */
        onBack(){ closeWith('[data-tour="wo-timesheet-head"]'); },
        prep(){ glideTo('[data-tour="wo-timesheet"]'); },
        title: 'One row per person',
        body: '<p>Everyone assigned to the job gets a row, and three columns of hours:</p>' +
              '<ul><li><b>Regular Time</b> — standard hours</li>' +
              '<li><b>Travel Time</b> — getting to and from site</li>' +
              '<li><b>Over Time</b> — hours beyond standard</li></ul>' +
              '<p>A new job shows <b>__:__</b> rather than zeros. That is normal: the row appears when ' +
              'someone is assigned, and fills in once hours exist.</p>',
        done: () => tsDay() },

      { target: '[data-tour="ts-grand"]', place: 'left', kind: 'info',
        title: 'The job’s total',
        body: '<p>The blue bar adds each column up across everyone, with the total for the whole job ' +
              'beside it.</p>' +
              '<p>It stays at <b>00:00</b> until somebody’s hours are in.</p>',
        done: () => tsDay() },

      /* The point of the article: hours can be entered here, but only in the
         Day view, which is exactly why it gets missed. */
      { target: '[data-tour="ts-view"]', place: 'left', noSkip: true,
        title: 'Day or Total',
        body: '<p>You have been reading <b>Total</b> — the whole job. <b>Day</b> shows one day at a ' +
              'time, and it is the only view hours can be <i>entered</i> in.</p>' +
              '<p>Technicians send their own hours from the field app. The office corrects them here.</p>',
        doIt: 'Click <b>Day</b>',
        done: () => tsDay() },

      { target: '[data-tour="ts-edit"]', extra: '[data-tour="ts-date"]', placeOnMain: true,
        /* Clicking the date itself opens a calendar, which hangs well outside
           the control it came from — without this the reader opened it and
           found it under the dim, on the one step that asks them to move
           between days. It belongs to the second lit area, not the first. */
        extraAlso: '.ts-cal',
        /* Two lit areas on one step, and placeOnMain anchors the tile on the
           first of them — so "left of the pencil" lands straight on top of
           the day picker, which is the other thing this step is about.

           The obstacle named here is the whole Timesheet card, not the day
           picker: the calendar hangs forty pixels to the LEFT of the control
           that opens it, so clearing the control alone still caught its
           corner, and clearing the calendar itself would move the tile every
           time it opened — which is the one thing placeOnMain exists to
           prevent. The card's left edge does not move whatever is open
           inside it, and it puts this tile in line with the three before it,
           which stand beside the rows. */
        avoid: '[data-tour="wo-timesheet"]',
        place: 'left', noSkip: true,
        /* Day was chosen on the step behind this one; Back puts the view back */
        onBack(){ closeWith('[data-tour="ts-total"]'); },
        title: 'Pick the day, then the pencil',
        body: '<p>The arrows and the date at the top move between days — days that already have hours ' +
              'on them are marked, and you cannot go past today.</p>' +
              '<p>The pencil on a row opens that person’s hours for that day. Hours are stored per ' +
              'person per day, not as clock-in and clock-out, so a correction is simply typing over ' +
              'them.</p>',
        /* No pencil anywhere: no wo:logTimesheet, or a Billed job without
           wo:logBilledTimesheet, or a job that has not started yet. */
        absent: {
          title: 'No pencil on these rows',
          body: '<p>Hours can be read on this job but not entered. There are three reasons that happens:</p>' +
                '<ul><li>your role does not include <b>Log Timesheet</b> — of the standard roles, ' +
                'Accountant, Clerical and Technician have it</li>' +
                '<li>the job is <b>Billed</b>, which needs <b>Log Billed Timesheet</b> as well, and only ' +
                'Accountant has that one</li>' +
                '<li>the job has not started yet — a day before the work order’s start date ' +
                'cannot be edited</li></ul>' +
                '<p>Everything above still reads the same way.</p>' },
        doIt: 'Click the pencil on a row',
        done: () => tsEditing() },

      /* Saving writes to a real work order and is logged as hours added or
         removed, so it is asked for rather than assumed — the same gate the
         create walkthrough puts in front of SUBMIT. */
      { target: '[data-tour="ts-fields"]', place: 'left', noSkip: true,
        noNext: () => !askedYes() && !askedNo(),
        ask: { target: '[data-tour="ts-edit"]', place: 'left',
          flag: 'This one is real',
          title: 'Save these hours for real?',
          body: '<p>The three boxes are yours to type in now — regular, travel and overtime for that ' +
                'person on that day.</p>' +
                '<p>Saving overwrites the day’s numbers on a real work order, and the change is ' +
                'written to the job’s history as hours logged or removed.</p>',
          yes: 'I’ll save it', no: 'Not on a real job' },
        title: () => (askedNo() ? 'Leave it as it was' : 'Type the hours, then save'),
        body: () => (askedNo()
          ? '<p>Press <b>Esc</b> to put the boxes back as they were and come out of edit mode. Nothing ' +
            'is sent.</p>'
          : '<p>Type into the three boxes, then click the tick where the pencil was. <b>Enter</b> saves ' +
            'as well, and <b>Esc</b> backs out of a box without saving it.</p>' +
            '<p>If nothing actually changed, nothing is sent and nothing is logged.</p>'),
        doIt: () => (askedNo() ? 'Press <b>Esc</b> to back out' : 'Save with the tick, or press <b>Enter</b>'),
        done: () => !tsEditing() },

      { kind: 'finish', when: () => !askedNo(), target: '[data-tour="ts-grand"]', place: 'left',
        title: 'That’s the timesheet',
        body: '<p>The day’s numbers are in, and the totals have moved with them.</p>' +
              '<p>Switch back to <b>Total</b> for the whole job. Anything that changed a total is in the ' +
              'job’s <b>History</b> at the bottom of the page, with who did it and when.</p>' },

      { kind: 'finish', when: askedNo, target: '[data-tour="ts-rows"]', place: 'left',
        title: 'Nothing was changed',
        body: '<p>You backed out, so nobody’s hours were touched.</p>' +
              '<p>That is the whole of it either way: <b>Total</b> to read the job, <b>Day</b> to put a ' +
              'day right, and the pencil on the row you need.</p>' }
    ]
  },

  'work-order-documents': {
    label: 'Add files to a work order',
    /* Same opening as assign and edit. The file picker itself is the
       operating system's and cannot be pointed at; the tour waits for the
       window to show the chosen files instead. */
    onHome: () => onWorkList() || onDetail(),
    begin(){ uploadMark = filesUploadedCount(); uploadKind = null; },
    reset(navigate){ navigate('/work?branch=0', { replace: true }); },
    midTask(){ return onDetail(); },
    steps: [
      { kind: 'intro',
        title: 'Let’s add a file to a work order',
        body: '<p>Three things: find the work order, pick the section the file belongs in, and upload it.</p>' +
              '<p>Photos, proposals, invoices and technical documents all work the same way.</p>' },

      findJobStep(),
      viewJobStep('add a file', () => uploadOpen() || uploaded()),

      /* An open step with a tile per section. The page is scrolled so all
         four Upload buttons are on screen (or as many as fit); the Photos
         tile is the main one, carrying the count and the buttons, and the
         other three are callouts. Clicking any Upload moves the tour on. */
      { dimOnly: '[data-tour="app-sidemenu"]', noSkip: true, id: 'upload-pick',
        target: '[data-tour="wo-upload-photos"]', place: 'left',
        /* prep() scrolls the page down to the Upload buttons; the step behind
           this one talks the reader through the page from the top */
        onBack(){ glideTop('[data-tour="wo-service-panel"]'); },
        /* fade: the Forms ring comes up and down inside the step, so it has to
           fade where it stands rather than blink out of existence */
        fade: true, hovers: true,
        /* the sections are as tall as the files in them, so on a work order
           with anything uploaded the lower Upload buttons start off screen */
        edges: true, edgeLabel: 'Photos/Videos',
        outline: () => ['[data-tour="wo-upload-photos"]', '[data-tour="wo-upload-proposals"]',
          '[data-tour="wo-upload-docs"]', '[data-tour="wo-upload-invoices"]',
          /* Forms is not what this walkthrough is about, and a ring that is
             always there would send the reader to a button it does not cover.
             It appears under the pointer and nowhere else. */
          (formsHover() ? '[data-tour="wo-add-form"]' : null)],
        /* Add Form opens its own window (AddFormModal), not an upload one;
           the tour steps aside while it is open rather than draw over it */
        yieldTo: '[data-tour="add-form-dialog"]',
        /* Land with the main tile halfway down the view. It used to try to
           fit all five Upload buttons on screen at once, which works on an
           empty work order and cannot on a busy one: every section grows with
           the files in it, and the span outruns the window. Then the scroll
           split the difference and the last sections fell off the bottom
           anyway, with nothing to say they were there. Now it centres the one
           control this tile belongs to, and the markers say where the rest
           went. */
        prep(){ glideTo('[data-tour="wo-upload-photos"]'); },
        title: 'Pick the section',
        /* each Upload button needs that section's add permission */
        body: () => (document.querySelector('[data-tour^="wo-upload-"]')
          ? '<p><b>Photos/Videos</b> — pictures and video from the job. Photos can be printed on the service ticket.</p>'
          : '<p>There are no <span class="k">Upload</span> buttons on this work order, so your role does not ' +
            'include adding files.</p>'),
        callouts: [
          { target: '[data-tour="wo-upload-proposals"]', title: 'Proposals',
            body: '<p>Proposals and quotes for this job.</p>' },
          { target: '[data-tour="wo-upload-invoices"]', title: 'Invoices',
            body: '<p>Invoices for this job.</p>' },
          { target: '[data-tour="wo-upload-docs"]', title: 'Technical Documents',
            body: '<p>Spec sheets, wiring diagrams and manuals for the equipment.</p>' },
          /* from AddFormModal's three entry points */
          /* PRE-PRODUCTION: the last sentence becomes a link to the forms
             article once that article exists. On the pre-production merge
             checklist — do not ship this wording with nothing behind it. */
          { target: '[data-tour="wo-add-form"]', title: 'Forms', place: 'top',
            hover: true, card: 'forms',
            body: '<p><b>Add Form</b> attaches a digital form from your templates, or uploads a PDF or ' +
                  'photos of a paper form.</p>' +
                  '<p>Forms work differently enough that they are not covered here. There is a separate ' +
                  'article on adding a form to a work order.</p>',
            /* latched, not a plain hovering(): the reader has to be able to
               take the pointer off the button and onto this card without it
               going out from under them — there will be a link in it */
            hideWhen: () => !formsHover() }],
        doIt: 'Click <b>Upload</b> on the section you want',
        done: () => uploadOpen() || uploaded() },

      { target: '[data-tour="upload-dropzone"]', place: 'right', avoid: '[data-tour="modal-shell"]', noSkip: true,
        /* the upload window opened to get here; nothing is uploaded until
           Upload All, so closing it costs the reader nothing */
        onBack(){ closeWith('[data-tour="modal-close"]'); },
        title: 'Add your files',
        body: '<p>Drag files onto this box, or click it to choose them from your computer. You can pick ' +
              'several at once.</p>' +
              '<p>The file types this section accepts are listed underneath.</p>',
        doIt: 'Choose or drop a file',
        done: () => uploadStaged() || uploaded() },

      /* Two lit areas, not one: the file cards, and Add More down in the
         footer under them. A single merged box would have swallowed Cancel and
         Upload All, which sit on that same row and are the next step's job.
         Both boxes are measured every tick, so the cards' area grows with each
         file added until the window hits its height and starts scrolling. */
      { target: '[data-tour="modal-body"]', also: '[data-tour="modal-shell"] ul',
        extra: '[data-tour="upload-add-more"]',
        place: 'right', avoid: '[data-tour="modal-shell"]', kind: 'info',
        title: 'Check what you’re adding',
        body: () => (uploadKind === 'photos' || uploadKind === 'docs'
          ? '<p>Each file gets its own card. Open it to set a <b>Name</b>, <b>Tags</b> and a <b>Description</b>, ' +
            'and choose whether it prints on the service ticket with <b>Include In Service Ticket</b>.</p>' +
            (uploadKind === 'photos' ? '<p>Videos cannot go on the service ticket.</p>' : '')
          : '<p>Each file is listed with its size. The trash icon takes one off before it is uploaded.</p>') +
          '<p><span class="k">Add More</span>, bottom left, adds more files to the same upload.</p>',
        done: () => uploaded() },

      { target: '[data-tour="upload-all"]', place: 'right', avoid: '[data-tour="modal-shell"]', noSkip: true,
        title: 'Upload',
        body: '<p>Nothing is saved until you click it. The number in brackets is how many files will go up.</p>',
        doIt: 'Click <b>Upload All</b>',
        done: () => uploaded() },

      { kind: 'finish', place: 'top',
        /* Upload All closed the window, so the three steps behind this one are
           all describing something that is no longer on the screen. Back goes
           to the section picker — the last screen still standing, and the one
           to start from to add another file. */
        backTo: 'upload-pick',
        /* Uploading one file is rarely the whole job — a set of photos, then
           the proposal. Rather than make the reader end the walkthrough and
           start it again, send them round from the section list. */
        again: { label: 'Add another', to: 'upload-pick' },
        target: () => '[data-tour="wo-sec-' + (uploadKind || 'photos') + '"]',
        /* A section grows with the files in it, so on a busy work order this
           one is taller than the screen and centring it put both of its ends
           off the view. Align the top: the section's heading is what the tile
           is talking about, and it is the end the tile anchors to. The newest
           file, at the bottom of the section, stays below the fold — the cure
           for that is pointing at the file itself, which needs an anchor on
           it that the app does not have yet. */
        prep(){ glideTo('[data-tour="wo-sec-' + (uploadKind || 'photos') + '"]', { align: 'top' }); },
        title: 'It’s on the work order',
        body: () => '<p>Your file is in <b>' + uploadLabel() + '</b>.</p>' +
          (uploadKind === 'photos'
            ? '<p>Each photo card keeps its <b>Include In Service Ticket</b> toggle, for changing whether it ' +
              'prints later.</p>'
            : uploadKind === 'docs' ? ''
            : '<p>Click its name to open it; Download and Delete are on the right of its row.</p>') +
          '<p>If that was the only file you needed, press <b>Done</b> to finish here. To add another — ' +
          'to this section or any other — press <b>Add another</b> and I’ll take you back to the list of ' +
          'sections.</p>' }
    ]
  }
};

/* ---------------- badge state (read by header.js) ---------------- */
export const wtBadgeState = () => SESSION.getItem('makaniKbWtBadge') || '';   /* '' | 'done' | 'paused' */
export const wtBadgeSlug = () => SESSION.getItem('makaniKbWtSlug') || '';
const setBadge = (state, slug) => {
  SESSION.setItem('makaniKbWtBadge', state || '');
  if (slug) SESSION.setItem('makaniKbWtSlug', slug);
  announce();
};

/* ---------------- the component ---------------- */
export default function KbWalkthrough(){
  const navigate = useNavigate();
  const location = useLocation();
  const root = useRef(null);
  const eng = useRef(null);

  /* The engine lives in a ref so its geometry work never goes through
     React. It is created once and given the latest navigate each render. */
  if (!eng.current) eng.current = createEngine();
  eng.current.navigate = navigate;

  useEffect(() => {
    const e = eng.current;
    e.mount(root.current);
    const onStart = ev => e.start(ev.detail.slug, 'auto');
    const onResume = ev => e.start(ev.detail.slug, ev.detail.resume);
    const onKey = ev => { if (e.run && ev.key === 'Escape'){ ev.preventDefault(); e.end('paused'); } };
    window.addEventListener(KB_WALK_START, onStart);
    window.addEventListener('makani-kb-walk-resume', onResume);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener(KB_WALK_START, onStart);
      window.removeEventListener('makani-kb-walk-resume', onResume);
      document.removeEventListener('keydown', onKey);
      e.unmount();
    };
  }, []);

  /* A route change while running: the target may have moved or vanished. */
  useEffect(() => { if (eng.current.run) eng.current.paint(); }, [location.pathname, location.search]);

  return (
    <div className="kb-wt" ref={root}>
      <div className="wt" data-kbwt="root">
        {/* Two dim layers. Holes are cut with clip-path, which cannot animate
            between shapes, so a step that wants its holes to open and close
            gently crossfades: the old shape lives on in the ghost and fades
            out while the new one fades in. */}
        <div className="wt-dim wt-ghost" />
        <div className="wt-dim" />
        <div className="wt-ring hide" />
        <div className="wt-ring wt-ring2 hide" />
        {/* outline-only rings for an open step; the engine owns the children */}
        <div className="wt-outlines" />
        <div className="wt-tip" />
        {/* callout tiles for a step with several controls; engine-owned too.
            After the main tile, so '.wt-tip' always finds the main one first. */}
        <div className="wt-callouts" />
        {/* markers at the edge of the view for tags that are scrolled out of
            it — engine-owned as well */}
        <div className="wt-edges" />
        {/* the gate card, for a step that asks before it lets the reader on */}
        <div className="wt-ask hide" />
      </div>
    </div>
  );
}

/* ---------------- the engine ---------------- */
function createEngine(){
  const E = {
    run: null, slug: null, step: 0, prepped: -1, navigate: null,
    els: null, watch: 0, repaintQueued: false, cameFrom: {},
  };

  E.mount = root => {
    E.els = {
      wt: root.querySelector('.wt'), dim: root.querySelector('.wt-dim:not(.wt-ghost)'), ghost: root.querySelector('.wt-ghost'),
      ring: root.querySelector('.wt-ring'), ring2: root.querySelector('.wt-ring2'),
      outlines: root.querySelector('.wt-outlines'), callouts: root.querySelector('.wt-callouts'),
      edges: root.querySelector('.wt-edges'),
      ask: root.querySelector('.wt-ask'), tip: root.querySelector('.wt-tip')
    };
    E.els.wt.addEventListener('click', E.onClick);
  };
  E.unmount = () => { E.stopWatch(); if (E.els) E.els.wt.removeEventListener('click', E.onClick); };

  /* Write markup only when it has actually changed — and never decide that by
     reading innerHTML back. The serializer does not round-trip a self-closed
     foreign tag: <path d="…"/> goes in and <path d="…"></path> comes out, so
     the comparison is always unequal and every icon-bearing element is rebuilt
     on every repaint. That rebuilt the main tile's buttons under the reader on
     any step with an icon, and replayed the edge markers' pop animation on
     every mousemove. Keep what was written beside the element and compare
     against that. Returns whether it changed. */
  const setHtml = (el, html) => {
    if (el._wtHtml === html) return false;
    el.innerHTML = html;
    el._wtHtml = html;
    return true;
  };

  const stepDef = () => (E.run ? E.run.steps[E.step] : null);

  /* ---- running ---- */
  E.start = (slug, resume) => {
    const w = WALKTHROUGHS[slug];
    if (!w) return;
    /* a fresh start ("Take me there"), as opposed to Resume from the badge */
    const fresh = resume === 'auto' || !resume;
    if (resume === 'auto') resume = !!(w.midTask && w.midTask());
    if (fresh && w.begin) w.begin();
    E.run = w; E.slug = slug; E.step = 0; E.reach = 0; E.held = null; E.prepped = -1; E.cameFrom = {};
    stepSince = Date.now(); E.calloutAt = {}; E.mainAt = null;
    E.placedStep = null; E.settleStep = null; clearTimeout(E.placeTimer); E.placeTimer = 0;
    E.lostSince = 0; clearTimeout(E.lostTimer); E.lostTimer = 0;
    setBadge('', slug);
    setLastArticle(slug); announce();          /* the article rides along in the drawer */
    setWalkthroughRunning(true);
    /* The drawer takes itself off screen for the duration — it reads
       isWalkthroughRunning, which setWalkthroughRunning above has just
       announced. It used to be dismissed from here instead, but only at a
       width where it crowded the tile, which left it cluttering a wide screen
       and meant writing to the reader's own drawer state to do it. */
    if (!resume && w.reset) w.reset(E.navigate);
    /* a walkthrough may have more than one home page — the assign one also
       runs on an open work order, which must not be navigated away from */
    else if (!(w.onHome ? w.onHome() : onWorkList())) E.navigate(w.leftFrom || '/work?branch=0', { replace: true });
    w.leftFrom = null;
    E.els.wt.classList.add('on');
    E.startWatch();
    /* the page may be mid-navigation; paint once the DOM has caught up */
    setTimeout(() => {
      if (!E.run) return;
      if (resume) E.enter(E.resumeStep());
      E.paint();
    }, 60);
  };

  E.end = how => {
    /* Where they were when they left. The landing rule (landingStep) reads
       the screen to work out how far they have got, which is right when the
       reader has been doing things on their own — but it cannot see a step
       that only explains something, so pausing on one of those and coming
       back landed them several tiles earlier. Remember the step itself. */
    if (how === 'paused' && E.run) writeJson('makaniKbWalkAt', { slug: E.slug, step: E.step });
    else SESSION.removeItem('makaniKbWalkAt');
    setBadge(how, E.slug);
    E.run = null; E.step = 0;
    clearTimeout(E.beatTimer); E.beatTimer = 0; E.dimPath = null; E.dimStep = null;
    E.els.wt.classList.remove('on');
    E.stopWatch();
    setWalkthroughRunning(false);
  };

  /* Revisiting. A step the reader went Back to is usually already done — the
     branch is still picked — so left to the watcher it would flick straight
     forward again, and Back would do nothing but flash. Any step behind the
     furthest one reached (E.reach) is therefore "held": it waits for a fresh
     action rather than a true done(). Fresh means done() goes false and back
     to true, or the page moves (a different branch changes the URL). Next
     always works. Steps at or past E.reach behave exactly as before, so
     resume and doing things out of order are untouched. */
  const doneAt = i => { const s = E.run.steps[i]; return !!(s && s.done && s.done()); };

  /* Branches. A step may carry when(): "this step belongs to the path the
     reader is on". Steps whose when() is false are not part of this run at
     all — skipped forward and backward, left out of the count — so one
     walkthrough can cover two routes (find a work order: the Work Orders tab
     or through the customer) without either reader seeing the other's steps. */
  const stepOn = i => { const s = E.run.steps[i]; return !!s && (!s.when || s.when()); };
  const nextOn = i => { let n = i; while (n < E.run.steps.length && !stepOn(n)) n++; return n; };
  const prevOn = i => { let n = i; while (n >= 0 && !stepOn(n)) n--; return n; };
  /* the reader's position and total, counting only their own path */
  E.counted = () => {
    let total = 0, at = 0;
    for (let i = 0; i < E.run.steps.length; i++){
      if (!stepOn(i)) continue;
      total++;
      if (i <= E.step) at = total;
    }
    return { at, total };
  };

  /* A jump: the reader is carried from one step to another that is not the
     next one. Where they came from is remembered, so Back is the way back
     rather than whatever step happens to be numerically before. */
  E.jumped = (from, to) => { E.enter(to); E.cameFrom[to] = from; };

  E.enter = i => {
    E.step = i;
    resetLatches();
    stepSince = Date.now();
    E.calloutAt = {}; E.mainAt = null;
    if (i < E.reach) E.held = { href: window.location.href, done: doneAt(i) };
    else { E.reach = i; E.held = null; }
  };

  E.next = () => {
    if (!E.run) return;
    const n = nextOn(E.step + 1);
    if (n >= E.run.steps.length){ E.end('done'); return; }
    delete E.cameFrom[n];                       /* walked to, so Back is simply back */
    E.enter(n); E.paint();
  };
  E.back = () => {
    if (!E.run) return;
    /* onBack: going back from here means undoing something first — the create
       form is open, and the step behind this one is about opening it */
    const cur = stepDef();
    if (cur && cur.onBack) cur.onBack(E.navigate);
    /* the step before, unless the reader was carried here from somewhere else */
    /* backTo: the step behind this one describes a window that has since been
       submitted and closed, so going back to it would point at nothing. Name
       the last step whose screen is still on the page and land there instead.
       It beats cameFrom, which would send the reader to the very step that has
       gone — finishWhen records the carry, and that carry is what is stale. */
    const named = cur && val(cur.backTo);
    const byId = named ? E.run.steps.findIndex(x => x.id === named) : -1;
    const jumped = E.cameFrom[E.step];
    const p = byId >= 0 && stepOn(byId) ? byId
      : (jumped != null && stepOn(jumped) ? jumped : prevOn(E.step - 1));
    delete E.cameFrom[E.step];
    if (p >= 0){ E.enter(p); E.paint(); }
  };

  /* Where a resume picks up: the step they paused on, if it still makes
     sense — its branch is still the one they are on, and anything it points
     at is on the screen. Otherwise work it out from the screen, below. */
  E.resumeStep = () => {
    const at = readJson('makaniKbWalkAt', null);
    if (at && at.slug === E.slug && at.step >= 0 && at.step < E.run.steps.length && stepOn(at.step)){
      const st = E.run.steps[at.step];
      if (!sel(st) || document.querySelector(sel(st))) return at.step;
    }
    return E.landingStep();
  };

  /* Where a resume should pick the reader up when there is nothing to go on:
     the first step still to do, walking back to the last one whose target is
     actually on screen. */
  E.landingStep = () => {
    const steps = E.run.steps;
    let i = nextOn((steps[0].kind === 'intro' && steps.length > 1) ? 1 : 0);
    while (i < steps.length - 1 && (!stepOn(i) || (steps[i].done && steps[i].done()))) i = nextOn(i + 1);
    const onScreen = n => !sel(steps[n]) || !!document.querySelector(sel(steps[n]));
    while (i > 0 && (!stepOn(i) || !onScreen(i))) i = prevOn(i - 1);
    return i;
  };

  /* Advances past every step whose done() already holds. A timer, not
     requestAnimationFrame, because rAF does not tick in every embedded
     browser and a watchdog that silently never runs is worse than none.
     Also repaints when the target's geometry changes, which covers the
     dialog scrolling and the table scrolling sideways. */
  let last = '';
  E.startWatch = () => {
    clearInterval(E.watch);
    E.watch = setInterval(() => {
      if (!E.run){ E.stopWatch(); return; }
      /* pauseWhen: the reader has plainly gone off to do their own thing (the
         find walkthrough: they opened the work order they were looking for).
         Fold into the header badge rather than fight them, and remember the
         list they left so Resume brings them back to it, search and all. */
      const home = E.run.onHome ? E.run.onHome() : onWorkList();
      if (home) E.lastHome = window.location.pathname + window.location.search;
      if (E.run.pauseWhen && E.run.pauseWhen()){ E.run.leftFrom = E.lastHome; E.end('paused'); return; }
      /* finishWhen: the reader has done the thing the whole walkthrough was
         for. Go straight to the finish from wherever they are — the steps in
         between only explain how to get here, and one of them being "held"
         after a Back must not strand the reader on it. */
      if (E.run.finishWhen && E.run.finishWhen()){
        const last = prevOn(E.run.steps.length - 1);
        const end = last >= 0 && E.run.steps[last];
        if (end && end.kind === 'finish' && last !== E.step){ E.jumped(E.step, last); E.paint(false, true); return; }
      }
      /* skipTo: what the reader has just done here makes the steps between
         this one and another pointless — picking a branch, when the next few
         tiles are only working towards that. Named, not counted, so inserting
         a step cannot silently change where it lands. */
      const here = E.run.steps[E.step];
      const to = here && here.skipTo && here.skipTo();
      if (to){
        const i = E.run.steps.findIndex(x => x.id === to);
        if (i >= 0 && i !== E.step){ E.jumped(E.step, i); E.paint(false, true); return; }
      }
      let moved = false, origin = null;
      /* the reader took the other route: leave that branch's steps behind */
      if (!stepOn(E.step)){
        const n = nextOn(E.step + 1);
        if (n < E.run.steps.length){ E.enter(n); E.paint(false, true); return; }
      }
      while (E.step < E.run.steps.length - 1){
        const cur = E.run.steps[E.step];
        /* only Next moves a manual step — unless the reader has plainly gone
           ahead of it (leaveWhen), e.g. opened a work order without searching */
        if (cur.manual && !(cur.leaveWhen && cur.leaveWhen())) break;
        const d = doneAt(E.step);
        /* The hold guards the step the reader is standing on, and only that
           one. Once their action has moved the tour along, the steps it has
           also satisfied are behind them — held or not. Applying a filter
           from the panel used to stop on the Apply tile, which the reader had
           just used, and then again on the tile after it. */
        if (E.held && !moved){
          if (!d){ E.held.done = false; break; }             /* undone: redoing it counts */
          if (E.held.done && window.location.href === E.held.href) break;
        } else if (!d) break;
        const n = nextOn(E.step + 1);
        if (n >= E.run.steps.length) break;
        /* Carried, not walked: Back returns them to where they were standing
           when the page moved the tour on — the step this run of advances
           started from, not wherever a previous one did. */
        if (origin === null) origin = E.step;
        E.enter(n); E.cameFrom[n] = origin; moved = true;
      }
      const b = E.rect(), b2 = b && E.rect2();
      const geo = r => (r ? [r.x, r.y, r.w, r.h].map(Math.round).join(',') : 'none');
      const s = E.run.steps[E.step];
      const yieldNow = !!(s.yieldTo && document.querySelector(s.yieldTo));
      const key = geo(b) + '|' + geo(b2) + '|' + (s.outline ? E.outlineKey(s) : '') + '|' + yieldNow + '|' + E.step + '|' + (E.held ? +doneAt(E.step) : '');
      if (moved || key !== last){ last = key; E.paint(false, true); }
    }, 100);
    document.addEventListener('scroll', E.onScroll, { capture: true, passive: true });
    window.addEventListener('resize', E.onScroll, { passive: true });
    document.addEventListener('mousemove', E.onMove, { capture: true, passive: true });
  };
  E.stopWatch = () => {
    clearInterval(E.watch); E.watch = 0;
    document.removeEventListener('scroll', E.onScroll, { capture: true });
    window.removeEventListener('resize', E.onScroll);
    document.removeEventListener('mousemove', E.onMove, { capture: true });
    mousePos = null;
  };
  /* hovers: the step has cards that come and go with the pointer. Repaint on
     a short throttle rather than on every move — the cards fade in and out,
     so a frame or two either way is not seen. */
  E.onMove = e => {
    mousePos = { x: e.clientX, y: e.clientY };
    const s = stepDef();
    if (!s || !s.hovers || E.hoverQueued) return;
    E.hoverQueued = true;
    setTimeout(() => { E.hoverQueued = false; if (E.run) E.paint(false, true); }, 60);
  };

  /* Scrolling. The 100ms watcher alone let rings and tiles trail the page by
     up to a tenth of a second, and their own position transitions made them
     glide after it. So a real scroll (any scroller — capture phase) repaints
     once per frame, and while scrolling everything snaps (.wt.scrolling).
     rAF with a timer fallback, because rAF does not tick in every embedded
     browser (see the watcher); whichever fires first paints. Where scroll
     events do not fire at all (the preview pane), the watcher still covers. */
  E.onScroll = () => {
    if (!E.run || !E.els) return;
    if (Date.now() > (E.quietScroll || 0)){                /* the reader's scroll, not a prep's */
      E.els.wt.classList.add('scrolling');
      clearTimeout(E.scrollEnd);
      E.scrollEnd = setTimeout(() => E.els && E.els.wt.classList.remove('scrolling'), 150);
    }
    if (E.scrollQueued) return;
    E.scrollQueued = true;
    const run = () => { if (!E.scrollQueued) return; E.scrollQueued = false; if (E.run) E.paint(false, true); };
    if (window.requestAnimationFrame) window.requestAnimationFrame(run);
    setTimeout(run, 16);
  };

  /* ---- geometry ---- */
  const union = (a, b) => {
    if (!a) return b; if (!b) return a;
    const x1 = Math.min(a.x, b.x), y1 = Math.min(a.y, b.y);
    const x2 = Math.max(a.x + a.w, b.x + b.w), y2 = Math.max(a.y + a.h, b.y + b.h);
    return { x: x1, y: y1, w: x2 - x1, h: y2 - y1 };
  };
  const boxOf = el => { const r = el.getBoundingClientRect(); return { x: r.left, y: r.top, w: r.width, h: r.height }; };
  /* The app's own header is fixed, and the page scrolls underneath it — so
     nothing in the document clips a control that has slid up behind it, and a
     ring or a card drawn there sits on top of the app's chrome looking like a
     mistake. Everything below the header is clipped to it; anything INSIDE it
     (the header's own search box) is left alone, or the tour could never point
     at the header at all. */
  const chromeEl = () => document.querySelector('[data-tour="app-header"]');
  const chromeTop = () => { const h = chromeEl(); return h ? h.getBoundingClientRect().bottom : 0; };
  /* A dialog is fixed with a z-index over the header's, so it is painted on
     top of the chrome rather than under it, and a control in its own title
     row is not hidden by anything. The service ticket preview is 95vh tall
     and centred, which puts Download PDF and Print in the same band as the
     app's header — clipped to it, their ring lost its top edge and sat
     slightly off the buttons it was drawn for. */
  const aboveChrome = el => {
    const h = chromeEl();
    if (!h) return false;
    const hz = Number(getComputedStyle(h).zIndex) || 0;
    for (let p = el; p && p !== document.body; p = p.parentElement){
      const cs = getComputedStyle(p);
      if (cs.position !== 'fixed') continue;
      const z = Number(cs.zIndex);
      if (Number.isFinite(z) && z > hz) return true;
    }
    return false;
  };
  const underChrome = el => { const h = chromeEl(); return !!h && !h.contains(el) && !aboveChrome(el); };
  /* the Assign window's menus render hidden for a frame while they measure */
  const visible = el => getComputedStyle(el).visibility !== 'hidden';

  /* Open popups the step names in also: — every match, not just the first,
     and only ones with a size (a closed list is either absent or 0×0). */
  E.popups = () => {
    const s = stepDef();
    if (!s || !s.also) return [];
    return [...document.querySelectorAll(s.also)].filter(visible)
      .map(el => clipToScrollers(el, boxOf(el)))
      .filter(b => b.w > 0 && b.h > 0);
  };
  /* What the ring outlines while a list is open: the list plus the field that
     opened it (its wrapper), as one control — so the ring never runs across
     the open list. The wrapper is skipped if it is the step's whole target. */
  E.popupRing = () => {
    const s = stepDef();
    if (!s || !s.also) return null;
    const target = sel(s) && document.querySelector(sel(s));
    let box = null;
    [...document.querySelectorAll(s.also)].filter(visible).forEach(el => {
      const b = clipToScrollers(el, boxOf(el));
      if (!(b.w > 0 && b.h > 0)) return;
      const wrap = el.parentElement;
      const withField = wrap && wrap !== target && !wrap.contains(target) ? union(b, clipToScrollers(wrap, boxOf(wrap))) : b;
      box = union(box, withField);
    });
    return box;
  };
  /* withPopups false gives the target alone — what the tooltip is placed
     against, so it does not jump every time a list opens */
  E.rect = (withPopups = true) => {
    const s = stepDef();
    if (!s || !sel(s)) return null;
    const el = document.querySelector(sel(s));
    if (!el) return null;
    let box = boxOf(el);
    if (s.targetEnd){ const e2 = document.querySelector(s.targetEnd); if (e2) box = union(box, boxOf(e2)); }
    box = clipToScrollers(el, box);
    /* an open picker hangs outside its trigger; the hole is the union, each
       popup clipped to its own scrollers (a list inside the scrolling create
       dialog really is cut off by it) */
    if (withPopups) E.popups().forEach(p => { box = union(box, p); });
    return box;
  };
  /* A second, separate hole — for a step about two controls that are not
     next to each other (the two sortable headings), where one union would
     light up everything in between. */
  E.rect2 = () => {
    const s = stepDef();
    /* extra may be a function: a second hole that only opens once something
       has happened (the results, once a search is in place) */
    const q = s && (typeof s.extra === 'function' ? s.extra() : s.extra);
    let box = q ? E.boxFor(q) : null;
    if (!box || box.w <= 0 || box.h <= 0) return null;
    /* extraAlso: what hangs out of the second target — the search box's
       suggestion list, which lives inside it but is absolutely positioned, so
       the box does not grow on its own. The list changes height with the
       number of matches; the watcher repaints on that, and the ring follows. */
    if (s.extraAlso){
      [...document.querySelectorAll(s.extraAlso)].filter(visible).forEach(x => {
        const b = clipToScrollers(x, boxOf(x));
        if (b.w > 0 && b.h > 0) box = union(box, b);
      });
    }
    return box;
  };
  const clipToScrollers = (el, box) => {
    /* what it measured before anything was cut off it, so a caller can tell a
       control that is small from one that is mostly out of view */
    const full = { w: box.w, h: box.h };
    if (underChrome(el)){
      const t = chromeTop();
      if (box.y < t){ const bottom = box.y + box.h; box = { x: box.x, y: t, w: box.w, h: Math.max(0, bottom - t) }; }
    }
    /* clip to whatever scrolls the target — a row wider than its table
       would otherwise light up empty space under the app's own menu.
       A position:fixed element escapes its ancestors' overflow (the Assign
       window's Team and Role menus), so it is not clipped by them. */
    if (getComputedStyle(el).position === 'fixed') return { x: box.x, y: box.y, w: box.w, h: box.h, full };
    let p = el.parentElement;
    /* An absolutely positioned element is NOT clipped by an ancestor's
       overflow unless that ancestor establishes its containing block — one
       that is itself positioned, or carries a transform or filter. Anything
       above the containing block clips it as usual; the static boxes below it
       do not, and on screen it hangs straight out of them.

       The day picker's calendar is the case: absolute, inside the timesheet
       card, which is overflow:hidden and static. It visibly overflows the
       card, and clipping it to the card cut the bottom off its highlight
       while the calendar itself carried on down the page. */
    let escaping = getComputedStyle(el).position === 'absolute';
    while (p && p !== document.body){
      const cs = getComputedStyle(p);
      if (escaping && (cs.position !== 'static' || cs.transform !== 'none' || cs.filter !== 'none')) escaping = false;
      if (!escaping && /auto|scroll|hidden/.test(cs.overflowX + ' ' + cs.overflowY)){
        const c = p.getBoundingClientRect();
        const x1 = Math.max(box.x, c.left), y1 = Math.max(box.y, c.top);
        const x2 = Math.min(box.x + box.w, c.right), y2 = Math.min(box.y + box.h, c.bottom);
        box = { x: x1, y: y1, w: Math.max(0, x2 - x1), h: Math.max(0, y2 - y1) };
      }
      /* Nothing above a fixed ancestor clips it. The upload windows are
         rendered inside their section's overflow:hidden card but are
         position:fixed, so walking on cut the dropzone down to the card. */
      if (cs.position === 'fixed') break;
      p = p.parentElement;
    }
    return { x: box.x, y: box.y, w: box.w, h: box.h, full };
  };

  /* Outline-only rings (outline: [selectors]) — every control the reader may
     use on an open step, drawn without a hole or a pulse. A control scrolled
     out of view gets no ring, and neither does one an open list is lying
     over: the ring sits above the page, so it would cut across the list. */
  /* One ring per selector, always the same ring (index = position in
     outline:), so hiding one never shifts the others onto new controls. */
  /* all:<selector> — every match as one region, for something that is a set
     of things rather than a control: a highlight down a column of work order
     numbers, or across whichever rows are holding a form back. */
  const boxFor = q => {
    if (q.slice(0, 4) === 'all:'){
      let box = null;
      [...document.querySelectorAll(q.slice(4))].filter(visible).forEach(el => {
        const b = clipToScrollers(el, boxOf(el));
        if (b.w > 0 && b.h > 0) box = union(box, b);
      });
      return box;
    }
    const el = document.querySelector(q);
    return el ? clipToScrollers(el, boxOf(el)) : null;
  };
  E.boxFor = boxFor;
  /* An outline entry is a selector, or { target, also } for a control that
     grows when it opens a list of its own — the three dropdowns on the Create
     PO form. Two things follow from that shape, and both are what a reader
     expects without being able to say why:

       its ring covers the control AND its own open list, so the highlight
       grows and shrinks with the list rather than sitting behind it;

       its ring goes away while SOMEBODY ELSE'S open list lies over it, so a
       long list dropping over the control below does not leave a highlight
       stranded on top of it. Its own list never counts against it — that is
       the case that would otherwise blink the ring out the moment it opened.

     A null entry is a ring that is not up at the moment, kept in place so the
     list stays the same length: each ring element is reused by index, and a
     shorter list would hand one control's ring to another. */
  const outlineParts = s => (val(s.outline) || []).map(e => {
    if (!e) return null;
    const q = typeof e === 'string' ? e : e.target;
    let box = q ? boxFor(q) : null;
    if (!box || box.w < 20 || box.h <= 0) return null;
    const own = [];
    if (e && e.also){
      [...document.querySelectorAll(e.also)].filter(visible).forEach(x => {
        const b = clipToScrollers(x, boxOf(x));
        if (b.w > 0 && b.h > 0){ own.push(b); box = union(box, b); }
      });
    }
    return { box, own };
  });
  const outlineBoxes = s => outlineParts(s).map(o => (o ? o.box : null));

  /* THE rings this step draws — and therefore THE holes. A highlight is a
     hole, so the two are the same list worked out once and handed to both.
     They used to be computed separately, which let a ring grow with an open
     list while its hole stayed the size of the closed control, leaving a
     strip of dim across the bottom of the highlight. */
  const OUTLINE_PAD = 6;
  const overlaps = (a, b) => !(a.x + a.w <= b.x || a.x >= b.x + b.w || a.y + a.h <= b.y || a.y >= b.y + b.h);
  const sameBox = (a, b) => Math.abs(a.x - b.x) < 1 && Math.abs(a.y - b.y) < 1 &&
                            Math.abs(a.w - b.w) < 1 && Math.abs(a.h - b.h) < 1;
  /* duck: whether an area covered by somebody else's open list drops out.
     A RING ducks — a line drawn underneath an open list reads as a mistake.
     A HOLE does not: taking the hole away puts the dim back over a control
     the reader can still see and still use, which is the thing the dim is
     never supposed to do. Both take their box from the same place, so the two
     can differ about whether to show an area but never about where it is. */
  const litBoxes = (s, popups, duck) => {
    const parts = outlineParts(s);
    const allOwn = parts.reduce((a, o) => (o ? a.concat(o.own) : a), []);
    return parts.map(o => {
      if (!o) return null;
      const b = { x: o.box.x - OUTLINE_PAD, y: o.box.y - OUTLINE_PAD,
                  w: o.box.w + OUTLINE_PAD * 2, h: o.box.h + OUTLINE_PAD * 2 };
      if (!duck) return b;
      /* its own list never counts against it — that is the case that would
         blink a ring out the instant the reader opened it */
      const others = allOwn.filter(x => o.own.indexOf(x) < 0)
        .concat((popups || []).filter(pp => !o.own.some(w => sameBox(w, pp))));
      return others.some(x => overlaps(b, x)) ? null : b;
    });
  };
  E.litBoxes = litBoxes;
  E.paintOutlines = (s, popups) => {
    const host = E.els.outlines;
    /* the same list the dim cuts its holes from, so a ring and its hole can
       never disagree about where it is or how big it has grown */
    const boxes = litBoxes(s, popups, true);
    while (host.children.length < boxes.length){
      const r = document.createElement('div'); r.className = 'wt-ring wt-outline hide'; host.appendChild(r);
    }
    [...host.children].forEach((r, i) => {
      const b = boxes[i];
      /* fade: the ring is never taken away, only faded out where it stands,
         so a step can bring its highlights up and down as it goes */
      if (s.fade){
        r.classList.remove('hide');
        r.classList.toggle('out', !b);
        if (b){ r.style.left = b.x + 'px'; r.style.top = b.y + 'px'; r.style.width = b.w + 'px'; r.style.height = b.h + 'px'; }
        return;
      }
      if (!b){ r.classList.add('hide'); return; }
      r.classList.remove('hide');
      r.style.left = b.x + 'px'; r.style.top = b.y + 'px'; r.style.width = b.w + 'px'; r.style.height = b.h + 'px';
    });
  };
  /* What the watcher compares to decide the step needs repainting: where
     every ring is, and where every open list is — its size, not just that it
     is open, so a suggestion list growing under the reader's typing brings
     the hole cut for it along at once. */
  E.outlineKey = s => outlineBoxes(s).map(b => (b ? [b.x, b.y, b.w, b.h].map(Math.round).join(',') : '-')).join(';') +
    '|' + E.popups().map(b => [b.x, b.y, b.w, b.h].map(Math.round).join(',')).join(';');

  /* Callouts (callouts: [{ target, title, body, place }]) — small tiles of
     their own, one per control, alongside the step's main tile (which carries
     the count and the buttons). Text only: they take no clicks, so nothing
     under them is blocked. Each sits beside its control on the first side
     that is on screen and clear of the main tile, the other callouts and
     every outlined control; one whose control is scrolled away is hidden. */
  /* Beside box b, on the first side that is on screen and clear of every
     rect in taken; failing that, the preferred side pulled onto the screen. */
  const placeBeside = (b, cw, ch, pref, taken) => {
    const W = window.innerWidth, H = window.innerHeight, gap = 12, edge = 12;
    const m = 8;                                   /* clear of the ring drawn round each control */
    const hit = (a, t) => !(a.x + a.w <= t.x - m || a.x >= t.x + t.w + m || a.y + a.h <= t.y - m || a.y >= t.y + t.h + m);
    const at = p => {
      if (p === 'left')  return { x: b.x - gap - cw, y: b.y + b.h / 2 - ch / 2 };
      if (p === 'right') return { x: b.x + b.w + gap, y: b.y + b.h / 2 - ch / 2 };
      if (p === 'top')   return { x: b.x + b.w - cw, y: b.y - gap - ch };
      /* under the left end, for a control as wide as the page (the branch
         bar): right-aligned, its callout ends up nowhere near what it names */
      if (p === 'bottom-left') return { x: b.x, y: b.y + b.h + gap };
      return { x: b.x + b.w - cw, y: b.y + b.h + gap };            /* bottom, right-aligned */
    };
    const top = chromeTop() + edge;
    const onScreen = r => r.x >= edge && r.y >= top && r.x + cw <= W - edge && r.y + ch <= H - edge;
    for (const p of [pref || 'left', 'bottom', 'top', 'right', 'left']){
      const r = at(p);
      if (onScreen(r) && !taken.some(t => hit({ x: r.x, y: r.y, w: cw, h: ch }, t))) return r;
    }
    const r = at(pref || 'left');
    return { x: Math.min(Math.max(edge, r.x), W - cw - edge), y: Math.min(Math.max(top, r.y), H - ch - edge) };
  };
  E.placeBeside = placeBeside;

  /* ---- markers for what has scrolled out of the view ----
     A step can light four controls down a long page, and on a work order with
     files already in it the sections are tall enough that the last of them is
     off the bottom before the reader has done anything. The tag is hidden —
     correctly, it belongs beside its control — but nothing then says the
     control is there at all, and the reader takes the step to be about the
     two sections they can see.

     So: a small marker on the edge of the view it went past, in line with the
     control along the horizontal, carrying the tag's own name. It is the tag's
     understudy — up only while the tag is not, and each hands over to the
     other as the reader scrolls. */

  /* The band the control can actually be seen in: the viewport, cut down by
     every scroller it sits inside. This is the edge the reader perceives, and
     it is often not the edge of the window — a page with its own scrolling
     panel ends well above the bottom of the screen. */
  const clipRectOf = el => {
    const W = window.innerWidth, H = window.innerHeight;
    let r = { top: 0, bottom: H, left: 0, right: W };
    if (getComputedStyle(el).position === 'fixed') return r;
    let p = el.parentElement;
    while (p && p !== document.body){
      const cs = getComputedStyle(p);
      if (/auto|scroll|hidden/.test(cs.overflowX + ' ' + cs.overflowY)){
        const c = p.getBoundingClientRect();
        r = { top: Math.max(r.top, c.top), bottom: Math.min(r.bottom, c.bottom),
              left: Math.max(r.left, c.left), right: Math.min(r.right, c.right) };
      }
      if (cs.position === 'fixed') break;
      p = p.parentElement;
    }
    return r;
  };

  /* null when the control is in view and its tag can speak for itself */
  const offEdge = q => {
    const el = q && document.querySelector(q);
    if (!el || !visible(el)) return null;
    const raw = boxOf(el);
    if (raw.w <= 0 && raw.h <= 0) return null;
    const clipped = clipToScrollers(el, raw);
    if (clipped.w > 0 && clipped.h > 0) return null;
    const r = clipRectOf(el);
    /* which way it went: measured against the middle of the band it scrolled
       out of, not the middle of the window, or a panel sitting high on the
       page reports everything below it as being off the top */
    const above = raw.y + raw.h / 2 < (r.top + r.bottom) / 2;
    return { side: above ? 'top' : 'bottom', x: raw.x + raw.w / 2,
             y: above ? r.top : r.bottom,
             /* how far past the edge: the nearest is the one the reader
                reaches first, which is what decides who leads a cluster */
             dist: Math.max(0, above ? r.top - (raw.y + raw.h) : raw.y - r.bottom) };
  };

  const edgePill = (side, label) =>
    '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.2" ' +
    'stroke-linecap="round" stroke-linejoin="round">' +
    (side === 'top' ? '<path d="M4 10l4-4 4 4"/>' : '<path d="M4 6l4 4 4-4"/>') +
    '</svg><span>' + label + '</span>';

  /* edges: true — one marker per tag, plus one for the main tile when the
     step names its control with edgeLabel. Markers that would sit on top of
     one another become a single marker instead; see the note on clustering. */
  E.paintEdges = s => {
    const host = E.els.edges;
    if (!host) return;
    const W = window.innerWidth, H = window.innerHeight, edge = 14, gap = 6;
    /* A hover card is left out, always. It is meant to be found by pointing
       at its control and not before — a marker advertising it at the edge of
       the screen is the opposite of that, and would draw the eye to the one
       control the step is deliberately not about. */
    const want = s.edges
      ? [...(s.edgeLabel ? [{ q: sel(s), label: s.edgeLabel }] : []),
         ...(val(s.callouts) || []).filter(d => !d.hover)
           .map(d => ({ q: d.target, label: d.title }))]
      : [];

    /* A hidden twin, used only to measure a marker at its full width before
       anything is drawn. Measuring a drawn marker would mean measuring
       whatever it currently says, which is the thing we are deciding. */
    let rule = host.querySelector('.wt-edge.wt-measure');
    if (!rule && want.length){
      rule = document.createElement('div');
      rule.className = 'wt-edge wt-measure';
      host.appendChild(rule);
    }

    const live = [];
    want.forEach((def, i) => {
      const off = rule && offEdge(def.q);
      if (!off) return;
      rule.innerHTML = edgePill(off.side, def.label);
      const w = rule.offsetWidth;
      live.push({ i, label: def.label, side: off.side, y: off.y, dist: off.dist, w,
        cx: off.x, x: Math.min(Math.max(w / 2 + edge, off.x), W - w / 2 - edge) });
    });

    /* ---- clustering ----
       Always from the full, unmerged widths. A merged marker is narrower than
       the markers it replaced, so if the merged width fed the next decision
       the overlap would vanish, the markers would split, overlap again, and
       flicker at the edge of the view. Measuring the full width every time
       keeps the decision independent of what is currently drawn.

       Overlap is not pairwise either: A can cover B and B cover C while A and
       C never touch, and all three are still one marker. Hence a sweep along
       the edge rather than comparing pairs — sort by x, and keep extending the
       group while the next marker starts before the group's right-hand end.
       Each edge is swept on its own: a marker at the top and one at the bottom
       cannot collide however their x lines up. */
    const groups = [];
    ['top', 'bottom'].forEach(side => {
      let g = null;
      live.filter(m => m.side === side).sort((m, n) => m.x - n.x).forEach(m => {
        const l = m.x - m.w / 2, r = m.x + m.w / 2;
        if (g && l <= g.right + gap){ g.members.push(m); g.right = Math.max(g.right, r); }
        else { g = { side, members: [m], right: r }; groups.push(g); }
      });
    });
    /* the nearest member leads: its name is the useful one to carry, because
       it is the one that comes back into view first */
    groups.forEach(g => { g.members.sort((m, n) => m.dist - n.dist); g.lead = g.members[0]; });
    /* a stable running order, so a group keeps the same element between
       repaints and does not animate for no reason */
    groups.sort((m, n) => m.lead.i - n.lead.i);

    const pills = [...host.querySelectorAll('.wt-edge:not(.wt-measure)')];
    while (pills.length < groups.length){
      const m = document.createElement('div');
      m.className = 'wt-edge hide';
      host.appendChild(m); pills.push(m);
    }
    pills.forEach((m, i) => {
      const g = groups[i];
      if (!g){ m.classList.add('hide'); m.classList.remove('on', 'morph'); return; }
      /* "Proposals +1": the nearest name, and how many are behind it. With one
         member the count is zero and it reads as the plain name, so merging
         and unmerging are the same line of code rather than two states. */
      const more = g.members.length - 1;
      const html = edgePill(g.side, g.lead.label + (more ? ' +' + more : ''));
      const fresh = m.classList.contains('hide');
      const morph = setHtml(m, html) && !fresh;
      /* shown before measuring, or offsetWidth is 0 and it lands off-centre */
      m.classList.remove('hide');
      const mw = m.offsetWidth, mh = m.offsetHeight;
      m.style.left = Math.min(Math.max(mw / 2 + edge, g.lead.cx), W - mw / 2 - edge) + 'px';
      m.style.top = (g.side === 'top' ? Math.max(g.lead.y, 0) + mh / 2 + edge
                                      : Math.min(g.lead.y, H) - mh / 2 - edge) + 'px';
      /* the pop belongs to arriving, not to every repaint while it stands */
      if (fresh){ m.classList.remove('on', 'morph'); void m.offsetWidth; }
      m.classList.add('on');
      if (morph){ m.classList.remove('morph'); void m.offsetWidth; m.classList.add('morph'); }
    });
  };

  E.paintCallouts = (s, mainTip) => {
    const host = E.els.callouts, list = val(s.callouts) || [];
    /* outlineBoxes keeps a null for a control scrolled out of view (so each
       ring stays on its own control); placement only wants the real ones */
    const taken = [mainTip, ...outlineBoxes(s).filter(Boolean)];
    while (host.children.length < list.length){
      const c = document.createElement('div'); c.className = 'wt-tip wt-callout hide'; host.appendChild(c);
    }
    [...host.children].forEach((c, i) => {
      const def = list[i];
      const el = def && document.querySelector(def.target);
      const b = el && clipToScrollers(el, boxOf(el));
      if (!b || b.w < 20 || b.h <= 0){ c.classList.add('hide'); return; }
      c.classList.remove('hide');
      /* hideWhen: the callout is in the way of something the reader has just
         opened (the search suggestions). Fade rather than vanish, and keep
         its place so it comes back where it was. */
      c.classList.toggle('duck', !!(def.hideWhen && def.hideWhen()));
      /* a card that comes and goes with the pointer fades and lifts, where an
         ordinary callout is simply there for the length of its step */
      c.classList.toggle('hover', !!def.hover);
      /* named so hoverLatch can find this card and ask whether the pointer has
         made it across — see hoveringCard */
      if (def.card) c.setAttribute('data-wt-card', def.card);
      else c.removeAttribute('data-wt-card');   /* the elements are reused step to step */
      setHtml(c, '<h4>' + def.title + '</h4>' + def.body);
      const cw = c.offsetWidth, ch = c.offsetHeight;
      /* Placed once, and from then on it simply keeps its place beside its
         control: on a step that lights one thing after another, re-placing
         every callout each paint made the early ones shuffle about as the
         later ones arrived. If the control moves, the callout moves with it. */
      const fixes = E.calloutAt || (E.calloutAt = {});
      const fix = fixes[def.target];
      /* A hover card is only up while the pointer is on its own control, and
         only ever one at a time, so all it has to keep clear of is the main
         tile and the control it belongs to — on a step where half the page is
         lit, keeping clear of everything lit leaves it nowhere sensible to go.
         An ordinary callout stands for its whole step and keeps clear of the
         lot; for the same reason a hover card is not itself an obstacle. */
      const clear = def.hover ? [mainTip, b] : taken;
      const pos = fix ? { x: b.x + fix.dx, y: b.y + fix.dy } : placeBeside(b, cw, ch, def.place, clear);
      if (!fix) fixes[def.target] = { dx: pos.x - b.x, dy: pos.y - b.y };
      c.style.left = pos.x + 'px'; c.style.top = pos.y + 'px';
      if (!def.hover) taken.push({ x: pos.x, y: pos.y, w: cw, h: ch });
    });
  };

  /* The dim's shape. clip-path cannot animate between two different shapes,
     so a step whose holes should open and close gently (fade) hands the old
     shape to the ghost layer and crossfades the two. Every other step sets
     the shape outright, as it always has: the dim takes clicks, and a reader
     acting fast must not hit dim where the hole already is. */
  /* The crossfade belongs to moving from one step to the next — a light going
     out here and coming up there. Inside a step the shape snaps, as it does
     everywhere else: the holes follow a list growing under the reader's
     typing frame for frame, and the scrim never flickers lighter, which is
     what two layers at part opacity look like while they cross. */
  E.setDim = (path, fade) => {
    const { dim, ghost } = E.els;
    if (path === E.dimPath) return;
    const stepChanged = E.dimStep !== E.step;
    E.dimStep = E.step;
    if (fade && E.dimPath && stepChanged){
      ghost.style.clipPath = E.dimPath;
      ghost.style.transition = 'none'; ghost.style.opacity = '1';
      dim.style.transition = 'none'; dim.style.opacity = '0';
      void ghost.offsetWidth;                                  /* start from there, not from wherever */
      ghost.style.transition = 'opacity .36s cubic-bezier(.4,0,.2,1)'; ghost.style.opacity = '0';
      dim.style.transition = 'opacity .36s cubic-bezier(.4,0,.2,1)'; dim.style.opacity = '1';
    } else if (!fade){
      ghost.style.opacity = '0'; dim.style.transition = 'none'; dim.style.opacity = '1';
    }
    dim.style.clipPath = path; E.dimPath = path;
  };

  /* The gate card. It sits beside the control it guards — which has no hole
     while the card is up, so the dim still blocks it — and takes clicks of
     its own. Answered, it goes away and the step decides what to light. */
  E.paintAsk = s => {
    const host = E.els.ask, def = s && s.ask;
    const el = def && !askAnswer && document.querySelector(def.target);
    const b = el && clipToScrollers(el, boxOf(el));
    if (!b || b.w < 10 || b.h <= 0){ host.classList.add('hide'); return; }
    host.classList.remove('hide');
    const html = (def.flag ? '<div class="wt-flag">' + def.flag + '</div>' : '') +
      '<h4>' + def.title + '</h4>' + def.body +
      '<div class="wt-foot"><span class="sp"></span>' +
      '<button class="wt-btn line" data-wt="ask-no" type="button">' + def.no + '</button>' +
      '<button class="wt-btn go" data-wt="ask-yes" type="button">' + def.yes + '</button></div>';
    setHtml(host, html);
    const pos = placeBeside(b, host.offsetWidth, host.offsetHeight, def.place || 'top', []);
    host.style.left = pos.x + 'px'; host.style.top = pos.y + 'px';
  };

  /* ---- painting ---- */
  E.paint = (scrollIntoView, isFollowUp) => {
    if (!E.run) return;
    const s = stepDef();
    /* prep runs once per step — but not before the step's target exists, or the
       finish step would try to scroll to a row the list has not fetched yet */
    /* the scroll a prep does is the tour's own, not the reader's: it must not
       switch off the tile's glide to its new place (see E.onScroll) */
    if (E.prepped !== E.step && (!sel(s) || document.querySelector(sel(s)))){
      E.prepped = E.step;
      if (s.popIn) popInTiles();                           /* arrive, with no scroll to arrive from */
      if (s.prep){ E.quietScroll = Date.now() + 250; s.prep(); }
    }
    /* during(): unlike prep, this runs every paint — for a step that keeps
       playing after it has arrived and has to bring its next stop into view */
    if (s.during){ E.quietScroll = Date.now() + 250; s.during(); }
    const pad = 6, WT_R = 9;
    /* dock may be a function too: a choreographed step starts centred and
       moves aside part way through */
    const dock = val(s.dock);
    /* up to two holes; one scrolled wholly out of view is dropped, so on a
       two-target step whichever heading is on screen keeps the ring */
    /* A sliver at a scroller's edge is not "shown". A fixed width was the
       wrong test for that: the timesheet's pencil is an 18px icon and was
       read as a sliver, so its hole was never cut and the dim sat over the
       one control the step asks for. What tells them apart is how much of
       the control survived being clipped — a sliver has lost most of itself,
       a small button has lost nothing. */
    const shown = b => !!b && b.h > 0 && b.w >= Math.min(20, (b.full ? b.full.w : b.w) * 0.6);
    /* The target alone: the ring goes round it and the tooltip is placed
       against it, so neither jumps when a dropdown opens. */
    const r1 = E.rect(false), r2 = r1 && E.rect2();
    const both = [r1, r2].filter(shown);
    const main = both[0] || r1, second = both[1] || null;
    /* the tooltip is placed against both holes together */
    /* placeOnMain: the second hole is a big region that comes and goes, and
       the tile must not jump across the screen when it does */
    let box = second && !s.placeOnMain ? union(main, second) : main;
    /* lookAt: the control the tile is to stand beside, which is not the same
       thing as the control it is lighting up. A step that walks the reader
       round the page moves the tile from one to the next and lights each one
       in its own time; target: would tie the two together. */
    if (!box && s.lookAt){
      const at = val(s.lookAt), el = at && document.querySelector(at);
      const b = el && clipToScrollers(el, boxOf(el));
      if (b && b.w >= 20 && b.h > 0) box = b;
    }
    const W = window.innerWidth, H = window.innerHeight;
    const px = n => n + 'px';
    const { wt, dim, ring, ring2, tip } = E.els;

    /* An app dialog with its own backdrop is up (the filter's date picker):
       step aside entirely until it closes. The watcher repaints on the change. */
    E.yielding = !!(s.yieldTo && document.querySelector(s.yieldTo));
    wt.classList.toggle('yield', E.yielding);
    /* A view step: the tour has just brought the reader to a new screen, and
       one tile says what is on it before the next task. No dim — the point is
       to look at the page — and only Next moves it (kind:'info'). */
    wt.classList.toggle('open', !!s.view);
    /* popIn: rings and callouts on this step fade up as they arrive rather
       than simply being there. playing: the step is still choreographing
       itself, so the tile takes its time moving. */
    wt.classList.toggle('popin', !!s.popIn);
    wt.classList.toggle('fading', !!s.fade);
    wt.classList.toggle('playing', !!(s.animateFor && since() < s.animateFor));

    const popups = (main || s.outline) ? E.popups() : [];
    const d = dim.getBoundingClientRect();
    const n = v => Math.round(v * 100) / 100;
    const outer = 'M0 0 H' + n(d.width) + ' V' + n(d.height) + ' H0 Z';
    const padded = b => ({ x: b.x - pad, y: b.y - pad, w: b.w + pad * 2, h: b.h + pad * 2 });
    /* a rounded rect; rt / rb let a popup's hole meet its target's square */
    const rrect = (b, rt, rb) => {
        const X = b.x - d.left, Y = b.y - d.top, w = b.w, h = b.h;
        const a = Math.max(0, Math.min(rt, w / 2, h / 2)), c = Math.max(0, Math.min(rb, w / 2, h / 2));
        return 'M' + n(X + a) + ' ' + n(Y) +
          ' H' + n(X + w - a) + ' A' + n(a) + ' ' + n(a) + ' 0 0 1 ' + n(X + w) + ' ' + n(Y + a) +
          ' V' + n(Y + h - c) + ' A' + n(c) + ' ' + n(c) + ' 0 0 1 ' + n(X + w - c) + ' ' + n(Y + h) +
          ' H' + n(X + c) + ' A' + n(c) + ' ' + n(c) + ' 0 0 1 ' + n(X) + ' ' + n(Y + h - c) +
        ' V' + n(Y + a) + ' A' + n(a) + ' ' + n(a) + ' 0 0 1 ' + n(X + a) + ' ' + n(Y) + ' Z';
    };
    /* dimOnly wins even when the step has a target: the target is then only
       what the tile is placed against (the callout step), not a hole */
    /* The rule, everywhere: if a ring is drawn round something, the dim does
       not lie over it. A highlight says "this is yours to use", and dim over
       any part of it says the opposite — so every outlined area is cut out as
       well as ringed, on every step, without the step asking. */
    const hits = (a, b) => !(a.x + a.w <= b.x || a.x >= b.x + b.w || a.y + a.h <= b.y || a.y >= b.y + b.h);
    /* Overlapping holes cancel each other back to dim under evenodd (a sorting
       heading lives inside the table), so anything that touches is merged into
       one box before it is cut. */
    const mergeBoxes = list => {
      const out = [];
      list.forEach(b => {
        let cur = b;
        for (let i = out.length - 1; i >= 0; i--){ if (hits(cur, out[i])){ cur = union(cur, out[i]); out.splice(i, 1); } }
        out.push(cur);
      });
      return out;
    };
    /* Merging is only safe when the two boxes are near enough the same shape.
       A tall narrow dropdown lying over a wide table is not: their bounding
       box is the whole page, which is how the search list on the find tour
       came to cut a hole from the table up to the top of the window.

       So: a minus b, as up to four rectangles — the bands above and below the
       overlap, then the strips left and right of it. Applied in turn it takes
       a box down to only the parts nothing has cut already. */
    const minus = (a, b) => {
      if (!hits(a, b)) return [a];
      const out = [];
      const ax2 = a.x + a.w, ay2 = a.y + a.h, bx2 = b.x + b.w, by2 = b.y + b.h;
      if (b.y > a.y) out.push({ x: a.x, y: a.y, w: a.w, h: b.y - a.y });
      if (by2 < ay2) out.push({ x: a.x, y: by2, w: a.w, h: ay2 - by2 });
      const y1 = Math.max(a.y, b.y), y2 = Math.min(ay2, by2);
      if (y2 > y1){
        if (b.x > a.x) out.push({ x: a.x, y: y1, w: b.x - a.x, h: y2 - y1 });
        if (bx2 < ax2) out.push({ x: bx2, y: y1, w: ax2 - bx2, h: y2 - y1 });
      }
      return out.filter(r => r.w > 1 && r.h > 1);
    };
    /* Every box cut down to the part no earlier box has taken, so nothing
       overlaps: under evenodd two overlapping holes cancel back to dim, which
       is why they cannot simply all be drawn. First come, first served — so
       put the big ones first and they keep their rounded corners; a piece of
       a later box is drawn square, because its edges are joins, not edges. */
    const disjointBoxes = list => {
      const out = [];
      list.forEach(b => {
        let parts = [b];
        out.forEach(o => { parts = parts.reduce((acc, r) => acc.concat(minus(r, o)), []); });
        const whole = parts.length === 1 && parts[0].w >= b.w - 1 && parts[0].h >= b.h - 1;
        parts.forEach(r => out.push({ x: r.x, y: r.y, w: r.w, h: r.h, whole }));
      });
      return out;
    };
    const lit = litBoxes(s, popups, false).filter(Boolean);   /* already padded; holes never duck */
    if (!main || s.dimOnly){
      /* An open step (dimOnly): the dim covers just the one element named,
         the app's own side menu, and the rest of the page is left usable. */
      const only = s.dimOnly && document.querySelector(s.dimOnly);
      if (only){
        const m = boxOf(only);
        const shape = 'M' + (m.x - d.left) + ' ' + (m.y - d.top) + ' h' + m.w + ' v' + m.h + ' h' + (-m.w) + ' Z';
        /* only the part of a lit area that falls on the menu needs cutting;
           a hole drawn outside the shape would paint a second dark rectangle */
        const inside = mergeBoxes(lit.map(b => {
          const x1 = Math.max(b.x, m.x), y1 = Math.max(b.y, m.y);
          const x2 = Math.min(b.x + b.w, m.x + m.w), y2 = Math.min(b.y + b.h, m.y + m.h);
          return { x: x1, y: y1, w: x2 - x1, h: y2 - y1 };
        }).filter(b => b.w > 0 && b.h > 0));
        E.setDim(inside.length
          ? 'path(evenodd, "' + shape + inside.map(b => ' ' + rrect(b, WT_R, WT_R)).join('') + '")'
          : 'path("' + shape + '")', s.fade);
      } else if (lit.length || popups.length){
        /* the lit areas first, biggest first, so the table keeps its shape and
           an open list is reduced to whatever hangs outside it */
        const order = [...lit].sort((a, b) => b.w * b.h - a.w * a.h);
        const pieces = disjointBoxes([...order, ...popups.map(padded)]);
        E.setDim('path(evenodd, "' + outer +
          pieces.map(b => ' ' + rrect(b, b.whole ? WT_R : 0, b.whole ? WT_R : 0)).join('') + '")', s.fade);
      } else E.setDim(s.dimOnly ? 'polygon(0 0, 0 0, 0 0)' : 'none', s.fade);
      ring.classList.add('hide');
    } else {
      const holeFor = (b, rg) => {
        const p = padded(b);
        rg.classList.remove('hide');
        rg.style.left = px(p.x); rg.style.top = px(p.y); rg.style.width = px(p.w); rg.style.height = px(p.h);
        return rrect(p, WT_R, WT_R);
      };
      let path = outer + ' ' + holeFor(main, ring);
      if (second) path += ' ' + holeFor(second, ring2);
      /* Each open list gets its own hole, butted against the target's rather
         than merged into one big rectangle — a union would light up every row
         beside the list too. Under evenodd two overlapping holes cancel back
         to dim, so the part of the list already inside the target's hole is
         trimmed off, and the join is square. */
      const M = padded(main);
      popups.forEach(pb => {
        const P = padded(pb);
        const overlapX = P.x < M.x + M.w && P.x + P.w > M.x;
        const overlapY = P.y < M.y + M.h && P.y + P.h > M.y;
        if (!(overlapX && overlapY)){ path += ' ' + rrect(P, WT_R, WT_R); return; }
        if (P.y + P.h > M.y + M.h){                         /* hangs below: the usual case */
          const top = M.y + M.h;
          path += ' ' + rrect({ x: P.x, y: top, w: P.w, h: P.y + P.h - top }, 0, WT_R);
        } else if (P.y < M.y){                              /* opens upward */
          path += ' ' + rrect({ x: P.x, y: P.y, w: P.w, h: M.y - P.y }, WT_R, 0);
        }                                                   /* else wholly inside: already lit */
      });
      /* And the rings this step draws elsewhere on the page (the stop tile
         points at the status badge and rings Assigned To). One already inside
         a hole is left alone: cutting it twice would cancel it back to dim. */
      const cut = [M, ...(second ? [padded(second)] : []), ...popups.map(padded)];
      mergeBoxes(lit).forEach(b => { if (!cut.some(c => hits(b, c))) path += ' ' + rrect(b, WT_R, WT_R); });
      E.setDim('path(evenodd, "' + path + '")', s.fade);
      /* with a list open, the ring leaves the target and outlines the list
         and its field instead — it slides there on its own transition */
      const pr = popups.length ? E.popupRing() : null;
      if (pr){
        const p = padded(pr);
        ring.style.left = px(p.x); ring.style.top = px(p.y); ring.style.width = px(p.w); ring.style.height = px(p.h);
      }
    }
    if (!second) ring2.classList.add('hide');
    E.paintOutlines(s, popups);
    /* pulse only where a click is being asked for */
    const live = !!(main && val(s.doIt) && !(E.held && doneAt(E.step)));
    ring.classList.toggle('live', live);
    /* a region lit alongside the control (placeOnMain) is outlined, not pulsed */
    ring2.classList.toggle('live', live && !!second && !s.placeOnMain);

    /* ---- the tooltip ---- */
    /* The count is of the reader's own branch, not every step written. At a
       fork the branches are still switched off, so the total would read as
       whatever is left (a bare "2 of 2" on the question itself) — until the
       reader picks a route, show the position without a total. */
    /* A walkthrough can end two ways: done, or stopped — the record itself is
       in a state the rest of the steps cannot work on, and saying so is more
       use than pointing at a button that is not there. Both close the tour. */
    const count = E.counted(), stopped = s.kind === 'stop', finish = s.kind === 'finish' || stopped;
    const undecided = count.at >= count.total && E.run.steps.some((_, i) => i > E.step && !stepOn(i));
    let h = finish
      ? '<div class="wt-check' + (stopped ? ' stop' : '') + '"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor">' +
        (stopped ? '<path d="M12 6.5v7"/><path d="M12 17.6h.01"/>' : '<path d="M4 12.5l5.5 5.5L20 7"/>') + '</svg></div>'
      : '<div class="wt-count">Step ' + count.at + (undecided ? '' : ' of ' + count.total) + ' · ' + E.run.label + '</div>';
    /* absent: the control this step is about is not on the page for this
       reader — no permission, the wrong status, a feature switched off. Say so
       plainly and let them on, rather than point confidently at nothing and
       ask for a click they cannot make. Steps used to hand-roll this as a
       ternary inside body, which is easy to forget and easy to get half right:
       the words changed but the "click it to carry on" line stayed. */
    const gone = !!(s.absent && (() => {
      const q = sel(s);
      return q && q.slice(0, 4) !== 'all:' && !document.querySelector(q);
    })());
    const head = gone ? s.absent.title : val(s.title);
    h += (head ? '<h4>' + head + '</h4>' : '') +
      (gone ? val(s.absent.body) : (typeof s.body === 'function' ? s.body() : s.body));
    /* a revisited step that is still done: nothing to do, so say so and offer Next */
    const already = !!E.held && doneAt(E.step);
    /* nothing is being asked for when the control is not there: no pulse, no
       "to carry on" line, and the footer falls through to Next */
    const doIt = gone ? null : val(s.doIt);
    /* goOn: one of a run of tiles that only explain something, where nothing
       on the page is being asked for — so say plainly what moves it on */
    if (s.goOn) h += '<div class="wt-do"><span class="dot"></span><span>Click <b>Next</b> to continue</span></div>';
    else if (doIt && s.manual) h += '<div class="wt-do"><span class="dot"></span><span>' + doIt + ', then click <b>Next</b> to continue</span></div>';
    else if (doIt && already) h += '<div class="wt-do"><span class="dot"></span><span>Already done — change it, or press <b>Next</b> to carry on</span></div>';
    else if (doIt) h += '<div class="wt-do"><span class="dot"></span><span>' + doIt + ' to carry on</span></div>';
    h += '<div class="wt-foot">';
    if (finish){
      /* again: the reader may have more of the same to do — another file on
         the same work order. Rather than end and start over, hand them back
         to the step where the round begins. */
      h += '<button class="wt-btn quiet" data-wt="article" type="button">Back to the article</button>' +
           '<span class="sp"></span>' +
           (s.again ? '<button class="wt-btn line" data-wt="again" type="button">' + s.again.label + '</button>' : '') +
           '<button class="wt-btn go" data-wt="close" type="button">Done</button>';
    } else {
      /* noDots: a narrow tile has no room for the run of dots as well as its
         buttons, and squeezing both in gave it a scrollbar. The count in the
         header still says where the reader is. */
      if (!s.noDots) h += '<div class="wt-dots">' + E.run.steps.map((_, i) => (stepOn(i)
        ? '<i class="' + (i === E.step ? 'on' : (i < E.step ? 'past' : '')) + '"></i>'
        : '')).join('') + '</div>';
      h += '<span class="sp"></span>';
      if (E.step > 0) h += '<button class="wt-btn quiet" data-wt="back" type="button">Back</button>';
      /* noNext: the tile hands the page back and waits. There is nothing to
         click on it because the walkthrough is watching what the reader does
         with the app instead — Back and Exit are still there. */
      if (val(s.noNext)) h += '';
      else if (s.kind === 'intro') h += '<button class="wt-btn go" data-wt="next" type="button">Start</button>';
      else if (s.kind === 'info' || !doIt || already || s.manual) h +='<button class="wt-btn go" data-wt="next" type="button">Next</button>';
      /* noSkip: the next step only makes sense once this one is really done */
      else if (!s.noSkip && !val(s.noNext)) h += '<button class="wt-btn line" data-wt="next" type="button">Skip</button>';
      h += '<button class="wt-btn quiet" data-wt="close" type="button" title="Leave the walkthrough">Exit</button>';
    }
    h += '</div>';
    /* Only when it has actually changed. A choreographed step repaints many
       times a second, and rewriting the tile every time would rebuild the
       buttons under a reader mid-click and restart the caret. */
    setHtml(tip, h);
    /* Wide enough for its own footer. The run of dots grows with the
       walkthrough, and past a dozen steps the dots and three buttons no
       longer fit 330px — which gave the tile a scrollbar along the bottom.
       Measure what is there and step up a size if it does not fit, rather
       than sizing every tile for the longest walkthrough we might write. */
    tip.classList.remove('wide');
    const foot = tip.querySelector('.wt-foot');
    if (!s.narrow && foot && foot.scrollWidth > foot.clientWidth + 1) tip.classList.add('wide');
    /* Lost target: the tile was placed on this step and its control has
       since vanished — SUBMIT closing the create dialog, a step before the
       tour moves on. Re-centring then heading for the next target was the
       finish's zigzag, so the tile stays where it is; only a target gone for
       over 1.5s (the reader closed the dialog and wandered off) re-centres. */
    if (sel(s) && !box && E.placedStep === E.step){
      if (!E.lostSince){
        E.lostSince = Date.now();
        /* nothing else repaints a steady "no target", so come back to re-centre */
        clearTimeout(E.lostTimer);
        E.lostTimer = setTimeout(() => { E.lostTimer = 0; if (E.run) E.paint(false, true); }, 1550);
      }
    } else { E.lostSince = 0; clearTimeout(E.lostTimer); E.lostTimer = 0; }
    const lost = !!E.lostSince && Date.now() - E.lostSince < 1500;
    /* center: false — a tile that starts in the middle and then moves keeps
       the ordinary width, so its size never changes mid-flight */
    if (!lost) tip.classList.toggle('center', !box && !dock && s.center !== false);
    tip.classList.toggle('finish', !!finish);
    tip.classList.toggle('stopped', !!stopped);
    tip.classList.toggle('narrow', !!s.narrow);

    /* ---- place it: preference, then whatever fits, measured from the
       obstacle (the dialog) rather than the target, so an inset target
       cannot be "cleared" by four pixels ---- */
    const tw = tip.offsetWidth, th = tip.offsetHeight, gap = 14, edge = 12;
    /* the docks that hang off the bottom of the screen rather than the top */
    const pinBottom = !!dock && dock !== 'left-top' && dock !== 'left-mid';
    let tx, ty;
    if (dock){
      /* Docked out of the way of an open step. true / 'right': the bottom
         right corner, left of the follow-along drawer when it is showing.
         'left': bottom left, for a step whose own controls live bottom right
         (the customer list's pager). 'left-top': over the app's side menu,
         level with the top of the lit area, for a step whose target fills the
         page — there is nowhere beside it that is not on top of something. */
      const drawer = document.querySelector('.kb.drawer.on');
      const right = drawer ? drawer.getBoundingClientRect().left : W;
      if (dock === 'left-top'){
        tx = edge; ty = Math.min(Math.max(edge, box ? box.y : edge), H - th - edge);
      } else if (dock === 'left-mid'){
        /* over the app's own menu, half way down: a step that hands the whole
           page back to the reader and only wants the tile out of the way */
        tx = edge; ty = Math.max(edge, (H - th) / 2);
      } else {
        tx = dock === 'left' ? Math.max(edge, box ? box.x : edge) : right - tw - 16;
        ty = H - th - 16;
      }
    }
    else if (!box){ tx = (W - tw) / 2; ty = Math.max(edge, (H - th) / 2); }
    else {
      const keep = s.avoid ? document.querySelector(s.avoid) : null;
      const kr = keep ? keep.getBoundingClientRect() : null;
      const R = kr ? Math.max(box.x + box.w, kr.right) : box.x + box.w;
      const L = kr ? Math.min(box.x, kr.left) : box.x;
      const B = kr ? Math.max(box.y + box.h, kr.bottom) : box.y + box.h;
      const T = kr ? Math.min(box.y, kr.top) : box.y;
      const fits = { bottom: B + gap + th < H - edge, top: T - gap - th > edge, left: L - gap - tw > edge, right: R + gap + tw < W - edge };
      const at = p => {
        let x, y;
        if (p === 'bottom'){ x = box.x + box.w / 2 - tw / 2; y = B + gap; }
        else if (p === 'top'){ x = box.x + box.w / 2 - tw / 2; y = T - gap - th; }
        else if (p === 'left'){ x = L - gap - tw; y = box.y + box.h / 2 - th / 2; }
        else { x = R + gap; y = box.y + box.h / 2 - th / 2; }
        return { x: Math.min(Math.max(edge, x), W - tw - edge), y: Math.min(Math.max(edge, y), H - th - edge) };
      };
      const order = [val(s.place) || 'bottom', 'bottom', 'top', 'right', 'left'];
      const use = order.find(p => fits[p]) || 'bottom';
      let a = at(use);
      /* Whatever won, it must not sit on the control the step is asking for.
         At a narrow window every placement can overlap the dialog, and the
         fallback then lands on the target itself — on the Submit step that
         covered the SUBMIT button. Shift to whichever side of the target has
         more room; covering fields already filled in is an acceptable cost. */
      const tb = { x: box.x - pad, y: box.y - pad, w: box.w + pad * 2, h: box.h + pad * 2 };
      const overlapsTarget = () => !(a.x + tw <= tb.x || a.x >= tb.x + tb.w || a.y + th <= tb.y || a.y >= tb.y + tb.h);
      if (overlapsTarget()){
        const above = tb.y, below = H - (tb.y + tb.h);
        const y = above >= below ? tb.y - gap - th : tb.y + tb.h + gap;
        a = { x: a.x, y: Math.min(Math.max(edge, y), H - th - edge) };
      }
      tx = a.x; ty = a.y;
      /* A step with callouts: the main tile is one of a family of tiles, so
         it is placed the way they are — clear of every outlined control, not
         only its own, or it lands on the next section's Upload button. */
      if ((val(s.callouts) || []).length){
        /* Placed once, then held at that offset from its control — the same
           way a callout is. Clamping it into the window on every repaint made
           it crab along the edge as the reader scrolled, and left it sitting
           beside a button that was no longer there. It is one of a family of
           tiles, so it travels like one. */
        const fix = E.mainAt;
        if (fix && E.placedStep === E.step){ tx = box.x + fix.dx; ty = box.y + fix.dy; }
        else {
          const q = E.placeBeside(box, tw, th, s.place, outlineBoxes(s).filter(Boolean));
          tx = q.x; ty = q.y;
          E.mainAt = { dx: tx - box.x, dy: ty - box.y };
        }
        /* ---- the catch ----
           It travels with its control, but only so far: the tile stays whole
           on the screen. Scroll past that point and it catches on the edge
           and waits there, and the marker at the edge of the view is what
           says where the control went.

           The whole card, not part of it. Holding back "just the buttons"
           was tried first and it is the wrong rule twice over — it asks how
           many pixels are enough every time the question comes up, and it
           leaves the reader a tile whose own text has slid out of view. The
           edge of the card is a boundary neither of us has to think about.

           Vertically it is clamped against the app's own header rather than
           the top of the window, because the page scrolls underneath the
           header and a tile tucked behind it is no more reachable than one
           off the screen entirely. */
        tx = Math.min(Math.max(edge, tx), W - tw - edge);
        ty = Math.min(Math.max(chromeTop() + edge, ty), H - th - edge);
      }
    }
    /* On a step change the tile holds still for a moment and then moves once.
       Placed straight away, it was sometimes aimed while the app was still
       re-laying the page, and the corrective pass re-aimed it mid-flight —
       a kink in the path. 40ms lets the layout settle; nobody sees the wait.
       The first placement of a run is immediate (there is no path to kink). */
    /* It also waits for the target to exist and hold still. The create
       walkthrough's finish points at the new row, which only appears when the
       list has reloaded, and then moves when the table scrolls to its Status
       column: aimed at each in turn, the tile zigzagged. So: hold until two
       paints 60ms apart agree on the target's box. A target still moving is
       given 1.5s; one that does not exist yet is waiting on the server (the
       list refetch took over 1.5s on devgav), so it is given 8s. */
    let settling = false;
    if (E.placedStep !== null && E.placedStep !== E.step){
      if (E.settleStep !== E.step){ E.settleStep = E.step; E.settleSince = Date.now(); E.settleKey = null; }
      const missing = !!sel(s) && !document.querySelector(sel(s));
      const key = box ? [box.x, box.y, box.w, box.h].map(Math.round).join(',') : 'none';
      const timedOut = Date.now() - E.settleSince > (missing ? 8000 : 1500);
      if (!timedOut && (missing || key !== E.settleKey)){
        settling = true;
        E.settleKey = key;
        clearTimeout(E.placeTimer);
        E.placeTimer = setTimeout(() => { E.placeTimer = 0; if (E.run) E.paint(false, true); }, 60);
      }
    }
    if (!settling && !lost){
      E.placedStep = E.step;
      tip.style.left = tx + 'px'; tip.style.top = ty + 'px';
      /* A bottom-docked tile is pinned by its bottom edge: coming from a
         centred tile its height measured now can be short, and a top-anchored
         tile would grow off the bottom of the screen. A top-left dock is
         anchored by its top, which is the point of it. */
      if (pinBottom){ tip.style.top = 'auto'; tip.style.bottom = '16px'; }
      else tip.style.bottom = '';
    }
    /* the tile sits where the target alone put it; if an open list lands
       under it, fade the tile out rather than cover options or hop sides */
    const onTip = popups.some(p => !(tx + tw <= p.x || tx >= p.x + p.w || ty + th <= p.y || ty >= p.y + p.h));
    tip.classList.toggle('duck', onTip);
    E.paintCallouts(s, { x: tx, y: pinBottom ? H - th - 16 : ty, w: tw, h: th });
    E.paintEdges(s);
    E.paintAsk(s);

    if (scrollIntoView && box){
      const el = document.querySelector(sel(s));
      if (el && (box.y < 0 || box.y + box.h > H)) el.scrollIntoView({ block: 'center' });
    }
    /* A choreographed step (animateFor) draws itself over a few seconds:
       repaint on a short timer while it plays, so whatever the step works out
       from the clock — which rings are lit, where the tile sits, how much of
       a line has been typed — simply turns up in the next paint. */
    clearTimeout(E.beatTimer); E.beatTimer = 0;
    if (s.animateFor && since() < s.animateFor){
      E.beatTimer = setTimeout(() => { E.beatTimer = 0; if (E.run) E.paint(false, true); }, 40);
    }
    /* one corrective pass after the current task — layout has not always
       settled when this runs from inside a re-render */
    if (!isFollowUp && !E.repaintQueued){
      E.repaintQueued = true;
      setTimeout(() => { E.repaintQueued = false; if (E.run) E.paint(false, true); });
    }
  };

  /* ---- clicks on the overlay ---- */
  E.onClick = e => {
    const act = e.target.closest && e.target.closest('[data-wt]');
    if (act){
      e.preventDefault(); e.stopPropagation();
      const a = act.dataset.wt;
      /* the gate: answering repaints, and the step takes it from there */
      if (a === 'ask-yes' || a === 'ask-no'){ askAnswer = a === 'ask-yes' ? 'yes' : 'no'; E.paint(); return; }
      if (a === 'next') E.next();
      else if (a === 'back') E.back();
      else if (a === 'close') E.end(stepDef() && /^(finish|stop)$/.test(stepDef().kind) ? 'done' : 'paused');
      else if (a === 'article'){ const slug = E.slug; E.end('done'); E.navigate(KB_BASE + '/a/' + encodeURIComponent(slug)); }
      /* Round again. begin() is re-run first, and it has to be: what counts as
         "done" for these steps is measured against a mark taken at the start —
         files uploaded since — and without a fresh mark every step behind the
         finish is still satisfied, so the watcher would carry the reader
         straight back to the finish they just left. */
      else if (a === 'again'){
        const def = stepDef(), to = def && def.again;
        const i = to ? E.run.steps.findIndex(x => x.id === to.to) : -1;
        if (i >= 0){
          if (E.run.begin) E.run.begin();
          E.enter(i); E.paint();
        }
      }
      return;
    }
    /* a click on the dim is a click on the wrong thing — say so */
    if (E.run && e.target.classList.contains('wt-dim')){
      [E.els.ring, E.els.ring2].forEach(ring => {
        ring.classList.remove('nudge'); void ring.offsetWidth; ring.classList.add('nudge');
        ring.addEventListener('animationend', () => ring.classList.remove('nudge'), { once: true });
      });
    }
  };

  return E;
}
